/**
 * Scanners Page
 * Vulnerability scanner management and results viewing
 */

import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { useDatePreferences } from '../hooks/useDatePreferences'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import jsPDF from 'jspdf'
import {
  Shield,
  Plus,
  Play,
  Trash2,
  Settings,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  X,
  RefreshCw,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Zap,
  Loader2,
  Target,
  Server,
  Globe,
  Network,
  ArrowUpDown,
  Filter,
  StopCircle,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  GitCompare,
  TrendingUp,
  TrendingDown,
  Minus,
  Link2,
  Unlink,
  Sparkles,
  ClipboardCheck,
  User,
  Calendar,
  FileEdit,
  ShieldCheck,
  ShieldAlert,
  History,
  Save,
  Ticket,
  Link,
  FileCode,
  FolderOpen,
  Webhook,
  BookOpen,
  Info,
  HelpCircle,
  Copy,
} from 'lucide-react'

type ScanConfig = {
  id: string
  name: string
  description: string | null
  scan_type: string
  targets: string
  frequency: string
  enabled: number
  last_run_at: number | null
  next_run_at: number | null
  total_scans: number
  last_scan_status: string | null
  scan_options?: string | { scan_mode?: 'quick' | 'full' | 'stealth' } | null
  created_at: number
  updated_at: number
}

type DiscoveredPort = {
  port: number
  protocol: string
  state: string
  service: string | null
  product: string | null
  version: string | null
}

type DiscoveredHost = {
  ip: string
  hostname: string | null
  state: string
  os?: string | null
  osAccuracy?: number | null
  ports: DiscoveredPort[]
}

type ScanResult = {
  id: string
  scan_config_id: string
  config_name: string
  scan_type: string
  started_at: number
  completed_at: number | null
  duration: number | null
  status: string
  hosts_scanned: number
  vulnerabilities_found: number
  critical_count: number
  high_count: number
  medium_count: number
  low_count: number
  info_count: number
  error_message: string | null
  discovered_hosts?: string | null // JSON string of DiscoveredHost[]
  // Scope fields (Issue #261)
  current_phase?: string | null
  subdomains?: string | null      // JSON array
  live_hosts?: string | null      // JSON array
  discovered_urls?: string | null // JSON array
  js_files?: string | null        // JSON array
  api_endpoints?: string | null   // JSON array
  directories?: string | null     // JSON array
}

// Vulnerability categories (Issue #261)
const VULN_CATEGORIES: Record<string, { label: string; description: string; color: string }> = {
  cve: { label: 'CVE', description: 'Known CVE vulnerabilities', color: 'bg-red-100 text-red-700' },
  misconfig: { label: 'Misconfig', description: 'Security misconfigurations', color: 'bg-orange-100 text-orange-700' },
  injection: { label: 'Injection', description: 'SQL, XSS, RCE, command injection', color: 'bg-purple-100 text-purple-700' },
  auth: { label: 'Auth', description: 'Authentication/authorization issues', color: 'bg-yellow-100 text-yellow-700' },
  takeover: { label: 'Takeover', description: 'Subdomain takeover vulnerabilities', color: 'bg-pink-100 text-pink-700' },
  ssl: { label: 'SSL/TLS', description: 'SSL/TLS certificate issues', color: 'bg-blue-100 text-blue-700' },
  network: { label: 'Network', description: 'Network service vulnerabilities', color: 'bg-cyan-100 text-cyan-700' },
  api: { label: 'API', description: 'API security issues', color: 'bg-indigo-100 text-indigo-700' },
  exposure: { label: 'Exposure', description: 'Sensitive data exposure', color: 'bg-amber-100 text-amber-700' },
  http: { label: 'HTTP', description: 'HTTP security issues', color: 'bg-gray-100 text-gray-700' },
}

// MITRE ATT&CK Enterprise Tactics (TA0001-TA0011)
// Maps vulnerability categories to MITRE tactics for kill chain analysis
const MITRE_TACTICS: Record<string, { id: string; name: string; color: string; description: string }> = {
  reconnaissance: { id: 'TA0043', name: 'Reconnaissance', color: 'bg-slate-500', description: 'Gathering information' },
  initial_access: { id: 'TA0001', name: 'Initial Access', color: 'bg-red-500', description: 'Gaining entry to network' },
  execution: { id: 'TA0002', name: 'Execution', color: 'bg-orange-500', description: 'Running malicious code' },
  persistence: { id: 'TA0003', name: 'Persistence', color: 'bg-amber-500', description: 'Maintaining foothold' },
  privilege_escalation: { id: 'TA0004', name: 'Privilege Escalation', color: 'bg-yellow-500', description: 'Gaining higher permissions' },
  defense_evasion: { id: 'TA0005', name: 'Defense Evasion', color: 'bg-lime-500', description: 'Avoiding detection' },
  credential_access: { id: 'TA0006', name: 'Credential Access', color: 'bg-emerald-500', description: 'Stealing credentials' },
  discovery: { id: 'TA0007', name: 'Discovery', color: 'bg-cyan-500', description: 'Learning about environment' },
  lateral_movement: { id: 'TA0008', name: 'Lateral Movement', color: 'bg-blue-500', description: 'Moving through network' },
  collection: { id: 'TA0009', name: 'Collection', color: 'bg-indigo-500', description: 'Gathering target data' },
  exfiltration: { id: 'TA0010', name: 'Exfiltration', color: 'bg-purple-500', description: 'Stealing data' },
  impact: { id: 'TA0040', name: 'Impact', color: 'bg-pink-500', description: 'Disruption/destruction' },
}

// Map vulnerability categories to MITRE tactics they could enable
const VULN_TO_MITRE: Record<string, string[]> = {
  cve: ['initial_access', 'execution', 'privilege_escalation'],
  misconfig: ['initial_access', 'defense_evasion', 'persistence'],
  injection: ['execution', 'privilege_escalation', 'credential_access'],
  auth: ['initial_access', 'credential_access', 'privilege_escalation'],
  takeover: ['initial_access', 'persistence', 'impact'],
  ssl: ['credential_access', 'defense_evasion'],
  network: ['initial_access', 'lateral_movement', 'discovery'],
  api: ['initial_access', 'collection', 'exfiltration'],
  exposure: ['reconnaissance', 'discovery', 'collection', 'exfiltration'],
  http: ['reconnaissance', 'discovery'],
}

// Get MITRE tactics for a vulnerability
function getMitreTactics(vuln: Vulnerability): Array<{ id: string; name: string; color: string }> {
  const category = getVulnCategory(vuln)
  const tacticKeys = VULN_TO_MITRE[category.key] || ['discovery']
  return tacticKeys.map(key => MITRE_TACTICS[key]).filter(Boolean)
}

// Aggregate MITRE tactics across all vulnerabilities for kill chain analysis
function aggregateMitreTactics(vulnerabilities: Vulnerability[]): Array<{
  tactic: { id: string; name: string; color: string; description: string }
  count: number
  severity: { critical: number; high: number; medium: number; low: number }
}> {
  const tacticCounts: Record<string, {
    count: number
    severity: { critical: number; high: number; medium: number; low: number }
  }> = {}

  // Exclude info-level findings from MITRE analysis (they're not attack vectors)
  const actualVulns = vulnerabilities.filter(v => v.severity !== 'info' && !v.is_false_positive)

  for (const vuln of actualVulns) {
    const category = getVulnCategory(vuln)
    const tacticKeys = VULN_TO_MITRE[category.key] || []

    for (const tacticKey of tacticKeys) {
      if (!tacticCounts[tacticKey]) {
        tacticCounts[tacticKey] = {
          count: 0,
          severity: { critical: 0, high: 0, medium: 0, low: 0 }
        }
      }
      tacticCounts[tacticKey].count++
      if (vuln.severity in tacticCounts[tacticKey].severity) {
        tacticCounts[tacticKey].severity[vuln.severity as keyof typeof tacticCounts[typeof tacticKey]['severity']]++
      }
    }
  }

  // Convert to array and sort by kill chain order
  const killChainOrder = [
    'reconnaissance', 'initial_access', 'execution', 'persistence',
    'privilege_escalation', 'defense_evasion', 'credential_access',
    'discovery', 'lateral_movement', 'collection', 'exfiltration', 'impact'
  ]

  return killChainOrder
    .filter(key => tacticCounts[key])
    .map(key => ({
      tactic: MITRE_TACTICS[key],
      count: tacticCounts[key].count,
      severity: tacticCounts[key].severity
    }))
}

// Scan modes (Issue #261)
const SCAN_MODES = [
  {
    id: 'quick',
    name: 'Quick Scan',
    description: 'Fast scan with critical/high severity focus. Best for quick security checks.',
    icon: Zap,
    details: ['Top 100 ports', 'Critical & High only', 'Fast templates'],
  },
  {
    id: 'full',
    name: 'Full Scan',
    description: 'Comprehensive scan with all severity levels. Best for thorough assessments.',
    icon: Shield,
    details: ['Top 100 ports', 'All severity levels', 'Comprehensive templates', 'Longer scan time'],
  },
  {
    id: 'stealth',
    name: 'Stealth Scan',
    description: 'Rate-limited scan with minimal footprint. Best for sensitive targets.',
    icon: Target,
    details: ['Common ports', 'Critical & High only', 'Rate limited', 'Minimal detection risk'],
  },
]

// Helper to get scan_mode from config's scan_options (Issue #261 - UI display)
const getScanModeFromOptions = (scan_options: string | { scan_mode?: 'quick' | 'full' | 'stealth' } | null | undefined): 'quick' | 'full' | 'stealth' => {
  if (!scan_options) return 'quick'
  const options = typeof scan_options === 'string' ? JSON.parse(scan_options) : scan_options
  return options?.scan_mode || 'quick'
}

// Scan mode display config
const SCAN_MODE_BADGES: Record<string, { label: string; color: string; icon: string }> = {
  quick: { label: 'Quick', color: 'bg-blue-100 text-blue-700', icon: '⚡' },
  full: { label: 'Full', color: 'bg-purple-100 text-purple-700', icon: '🔍' },
  stealth: { label: 'Stealth', color: 'bg-gray-100 text-gray-700', icon: '🥷' },
}

// Scanner Methodology Modal Component
interface MethodologyModalProps {
  isOpen: boolean
  onClose: () => void
}

const ScannerMethodologyModal = ({ isOpen, onClose }: MethodologyModalProps) => {
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Vulnerability Scanning Methodology</h2>
              <p className="text-sm text-gray-500">How scans work and why results differ from other tools</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Why Different Results */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Why Results May Differ From Other Scanners
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-3">
                This scanner uses <span className="font-semibold">validated vulnerability detection</span> rather than simple banner matching.
                This means fewer findings, but higher confidence that each finding is real.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Our Approach
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Active vulnerability verification</li>
                    <li>• Template-based detection with proof</li>
                    <li>• Reduces false positives significantly</li>
                    <li>• Each finding includes evidence</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    Traditional Scanners
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Banner/version matching only</li>
                    <li>• Reports "possible" vulnerabilities</li>
                    <li>• High false positive rates</li>
                    <li>• Requires manual verification</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Scan Types */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Scan Types</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-5 w-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">Quick Scan</span>
                </div>
                <p className="text-sm text-gray-700 mb-3">Fast security check for routine monitoring.</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Port discovery on common services</li>
                  <li>• Critical/High/Medium severity</li>
                  <li>• 4 vulnerability detection phases</li>
                  <li>• ~5-15 minute scan time</li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-800">Full Scan</span>
                </div>
                <p className="text-sm text-gray-700 mb-3">Comprehensive assessment for thorough coverage.</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Subdomain enumeration + DNS resolution</li>
                  <li>• Full port discovery + HTTP detection</li>
                  <li>• Web crawling + directory discovery</li>
                  <li>• 10 vulnerability detection phases</li>
                  <li>• All severity levels</li>
                  <li>• ~30-60 minute scan time</li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold text-purple-800">Stealth Scan</span>
                </div>
                <p className="text-sm text-gray-700 mb-3">Low-profile scan for sensitive environments.</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Rate-limited requests</li>
                  <li>• Common ports only</li>
                  <li>• Critical/High severity focus</li>
                  <li>• 2 detection phases</li>
                  <li>• Minimal detection footprint</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Scanning Phases */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Scanning Workflow (Full Scan)</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-4 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <h4 className="font-semibold text-cyan-800">Reconnaissance</h4>
                  <p className="text-sm text-gray-600">Discovers subdomains and validates which hosts are live via DNS resolution. Not all enumerated subdomains will resolve.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <h4 className="font-semibold text-green-800">Port Discovery</h4>
                  <p className="text-sm text-gray-600">Scans common ports to identify running services and detects which ports serve HTTP/HTTPS content.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <h4 className="font-semibold text-blue-800">Content Discovery</h4>
                  <p className="text-sm text-gray-600">Crawls web applications to find URLs, JavaScript files, API endpoints, and attempts to discover hidden directories.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex-shrink-0 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                <div>
                  <h4 className="font-semibold text-red-800">Vulnerability Detection</h4>
                  <p className="text-sm text-gray-600">Runs 10 specialized detection passes covering CVEs, misconfigurations, injections, authentication issues, subdomain takeover, SSL/TLS, network services, API security, and sensitive data exposure.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Severity Levels */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Severity Levels</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                <span className="w-3 h-3 rounded bg-red-600 inline-block mb-1"></span>
                <p className="font-semibold text-red-700 text-sm">Critical</p>
                <p className="text-xs text-gray-600">CVSS 9.0-10.0</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                <span className="w-3 h-3 rounded bg-orange-500 inline-block mb-1"></span>
                <p className="font-semibold text-orange-700 text-sm">High</p>
                <p className="text-xs text-gray-600">CVSS 7.0-8.9</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <span className="w-3 h-3 rounded bg-yellow-500 inline-block mb-1"></span>
                <p className="font-semibold text-yellow-700 text-sm">Medium</p>
                <p className="text-xs text-gray-600">CVSS 4.0-6.9</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <span className="w-3 h-3 rounded bg-blue-500 inline-block mb-1"></span>
                <p className="font-semibold text-blue-700 text-sm">Low</p>
                <p className="text-xs text-gray-600">CVSS 0.1-3.9</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                <span className="w-3 h-3 rounded bg-gray-400 inline-block mb-1"></span>
                <p className="font-semibold text-gray-700 text-sm">Info</p>
                <p className="text-xs text-gray-600">Informational</p>
              </div>
            </div>
          </section>

          {/* Important Notes */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Understanding Results</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span><span className="font-semibold">Subdomains vs Live Hosts:</span> Not all discovered subdomains will resolve. Only hosts with valid DNS records are scanned.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span><span className="font-semibold">Lower counts are normal:</span> Our validated approach means fewer but more accurate findings than banner-matching tools.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span><span className="font-semibold">Proof of concept:</span> High/Critical findings include evidence and reproduction steps where available.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span><span className="font-semibold">Network restrictions:</span> Firewalls and WAFs may block scanning. Low vulnerability counts may indicate good security OR blocked access.</span>
                </li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

type Vulnerability = {
  id: string
  host: string
  port: number | null
  service: string | null
  vulnerability_id: string | null
  vulnerability_name: string
  description: string | null
  severity: string
  cvss_score: number | null
  solution: string | null
  reference_urls: string | null
  is_false_positive: number
  false_positive_reason: string | null
  // Category (Issue #261)
  category?: string | null
  // Enriched fields from Vulners API (optional - may not exist on older data)
  affected_software?: string | null
  exploit_available?: number | null
  patch_available?: number | null
  // POC Validation fields
  exploitability_status?: 'unknown' | 'exploitable' | 'not_exploitable' | 'inconclusive' | 'validating' | null
  last_poc_validation_id?: string | null
  poc_validated_at?: number | null
  // POC Evidence fields (from JOIN with poc_validations)
  poc_validation_id?: string | null
  poc_result?: 'exploitable' | 'not_exploitable' | 'inconclusive' | 'error' | null
  poc_confidence_score?: number | null
  poc_validation_method?: string | null
  poc_output?: string | null
  poc_evidence_summary?: string | null
  poc_duration?: number | null
  // Remediation tracking fields (#122)
  remediation_status?: 'new' | 'acknowledged' | 'in_progress' | 'remediated' | 'accepted_risk' | 'false_positive' | 'deferred' | null
  assigned_to?: string | null
  assigned_to_username?: string | null
  remediation_due_date?: number | null
  remediation_notes?: string | null
  remediated_at?: number | null
  remediated_by?: string | null
  // Jira integration fields (#125)
  jira_ticket_key?: string | null
  jira_ticket_url?: string | null
  jira_ticket_status?: string | null
  jira_synced_at?: number | null
  // Proof data / IRR (Issue #261 - v62)
  proof_data?: string | null
}

type PocCredits = {
  balance: number
  updated_at: number
}

type Stats = {
  configs_count: number
  recent_scans: {
    total: number
    completed: number
    failed: number
    total_vulnerabilities: number
    total_critical: number
    total_high: number
    total_medium: number
  }
  vulnerability_breakdown: Array<{
    severity: string
    count: number
    false_positives: number
  }>
}

type Pagination = {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

const statusColors: Record<string, string> = {
  queued: 'text-gray-700 bg-gray-100',
  running: 'text-blue-700 bg-blue-100',
  completed: 'text-green-700 bg-green-100',
  failed: 'text-red-700 bg-red-100',
  cancelled: 'text-gray-700 bg-gray-100',
}

// Types for linked controls
type LinkedControl = {
  control_id: string
  linked_at: number
  notes: string | null
  control_number: string
  control_name: string
  control_description: string | null
  control_status: string
  framework_name: string
  linked_by_username: string | null
}

type SuggestedControl = {
  id: string
  control_number: string
  name: string
  description: string | null
  status: string
  frequency: string
  framework_name: string
  relevance_score: number
}

/**
 * VulnerabilityControlsSection Component (#240)
 * Displays linked controls and allows linking/unlinking controls to a vulnerability
 */
function VulnerabilityControlsSection({ vulnId }: { vulnId: string }) {
  const [linkedControls, setLinkedControls] = useState<LinkedControl[]>([])
  const [suggestedControls, setSuggestedControls] = useState<SuggestedControl[]>([])
  const [loading, setLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [linkingControlId, setLinkingControlId] = useState<string | null>(null)
  const [unlinkingControlId, setUnlinkingControlId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const toast = useToast()

  // Load linked controls when expanded
  useEffect(() => {
    if (expanded && linkedControls.length === 0) {
      loadLinkedControls()
    }
  }, [expanded])

  const loadLinkedControls = async () => {
    setLoading(true)
    try {
      const response = await api.getVulnerabilityLinkedControls(vulnId)
      if (response.success) {
        setLinkedControls(response.linked_controls)
      }
    } catch (error) {
      console.error('Failed to load linked controls:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSuggestedControls = async () => {
    setLoading(true)
    try {
      const response = await api.getVulnerabilitySuggestedControls(vulnId, 10)
      if (response.success) {
        setSuggestedControls(response.suggested_controls)
        setShowSuggestions(true)
      }
    } catch (error) {
      console.error('Failed to load suggested controls:', error)
      toast.error('Failed to load suggested controls')
    } finally {
      setLoading(false)
    }
  }

  const handleLinkControl = async (controlId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setLinkingControlId(controlId)
    try {
      const response = await api.linkVulnerabilityControl(vulnId, { control_id: controlId })
      if (response.success) {
        toast.success('Control linked successfully')
        // Reload linked controls and remove from suggestions
        await loadLinkedControls()
        setSuggestedControls(prev => prev.filter(c => c.id !== controlId))
      } else {
        toast.error(response.message || 'Failed to link control')
      }
    } catch (error) {
      console.error('Failed to link control:', error)
      toast.error('Failed to link control')
    } finally {
      setLinkingControlId(null)
    }
  }

  const handleUnlinkControl = async (controlId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setUnlinkingControlId(controlId)
    try {
      const response = await api.unlinkVulnerabilityControl(vulnId, controlId)
      if (response.success) {
        toast.success('Control unlinked')
        setLinkedControls(prev => prev.filter(c => c.control_id !== controlId))
      } else {
        toast.error(response.message || 'Failed to unlink control')
      }
    } catch (error) {
      console.error('Failed to unlink control:', error)
      toast.error('Failed to unlink control')
    } finally {
      setUnlinkingControlId(null)
    }
  }

  const controlStatusColors: Record<string, string> = {
    compliant: 'bg-green-100 text-green-700',
    'partially-compliant': 'bg-yellow-100 text-yellow-700',
    'non-compliant': 'bg-red-100 text-red-700',
    'not-assessed': 'bg-gray-100 text-gray-700',
  }

  return (
    <div className="pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => {
          e.stopPropagation()
          setExpanded(!expanded)
        }}
        className="flex items-center justify-between w-full text-left group"
      >
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-gray-500" />
          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Linked Controls
          </h5>
          {linkedControls.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
              {linkedControls.length}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {loading && linkedControls.length === 0 ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading controls...</span>
            </div>
          ) : (
            <>
              {/* Linked Controls List */}
              {linkedControls.length > 0 ? (
                <div className="space-y-2">
                  {linkedControls.map((control) => (
                    <div
                      key={control.control_id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <ClipboardCheck className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-sm text-gray-900">
                            {control.control_number}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                            controlStatusColors[control.control_status] || controlStatusColors['not-assessed']
                          }`}>
                            {control.control_status.replace('-', ' ')}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {control.control_name}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {control.framework_name}
                          {control.linked_by_username && (
                            <span> - Linked by {control.linked_by_username}</span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleUnlinkControl(control.control_id, e)}
                        disabled={unlinkingControlId === control.control_id}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Unlink control"
                      >
                        {unlinkingControlId === control.control_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Unlink className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-gray-500">
                  <ClipboardCheck className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                  <p>No controls linked to this vulnerability</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Link controls to track compliance impact
                  </p>
                </div>
              )}

              {/* Suggest Controls Button */}
              {!showSuggestions && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    loadSuggestedControls()
                  }}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50 w-full justify-center"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Suggest Relevant Controls
                </button>
              )}

              {/* Suggested Controls */}
              {showSuggestions && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h6 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Suggested Controls
                    </h6>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowSuggestions(false)
                        setSuggestedControls([])
                      }}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Hide
                    </button>
                  </div>
                  {suggestedControls.length > 0 ? (
                    <div className="space-y-2">
                      {suggestedControls.map((control) => (
                        <div
                          key={control.id}
                          className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200 hover:border-purple-300 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                              <span className="font-medium text-sm text-gray-900">
                                {control.control_number}
                              </span>
                              {control.relevance_score >= 90 && (
                                <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                                  High Match
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5 truncate">
                              {control.name}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {control.framework_name}
                            </p>
                          </div>
                          <button
                            onClick={(e) => handleLinkControl(control.id, e)}
                            disabled={linkingControlId === control.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                          >
                            {linkingControlId === control.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Link2 className="h-3 w-3" />
                            )}
                            Link
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3 text-sm text-gray-500 bg-gray-50 rounded-lg">
                      <p>No additional controls suggested</p>
                      <p className="text-xs text-gray-400 mt-1">
                        All relevant controls may already be linked
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * JiraTicketSection Component (#125)
 * Displays Jira ticket status and allows creating/syncing tickets
 */
function JiraTicketSection({ vulnId, vuln }: { vulnId: string; vuln: Vulnerability }) {
  const { formatDate } = useDatePreferences()
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [jiraConfigured, setJiraConfigured] = useState<boolean | null>(null)
  const [ticketInfo, setTicketInfo] = useState<{
    key: string | null
    url: string | null
    status: string | null
    synced_at: number | null
  }>({
    key: vuln.jira_ticket_key || null,
    url: vuln.jira_ticket_url || null,
    status: vuln.jira_ticket_status || null,
    synced_at: vuln.jira_synced_at || null,
  })
  const toast = useToast()

  // Check if Jira is configured on mount
  useEffect(() => {
    checkJiraConfig()
  }, [])

  const checkJiraConfig = async () => {
    try {
      const response = await api.getJiraConfig()
      setJiraConfigured(response.configured && response.config?.enabled === 1)
    } catch (error) {
      setJiraConfigured(false)
    }
  }

  const handleCreateTicket = async () => {
    setCreating(true)
    try {
      const response = await api.createJiraTicket(vulnId)
      if (response.success && response.ticket) {
        setTicketInfo({
          key: response.ticket.key,
          url: response.ticket.url,
          status: 'Open',
          synced_at: Date.now(),
        })
        toast.success(`Jira ticket ${response.ticket.key} created`)
      } else if (response.existing_ticket) {
        toast.error(`Ticket already exists: ${response.existing_ticket.key}`)
      } else {
        toast.error(response.error || 'Failed to create Jira ticket')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create Jira ticket')
    } finally {
      setCreating(false)
    }
  }

  const handleSyncTicket = async () => {
    setSyncing(true)
    try {
      const response = await api.syncJiraTicket(vulnId)
      if (response.success && response.ticket) {
        setTicketInfo({
          key: response.ticket.key,
          url: response.ticket.url,
          status: response.ticket.status,
          synced_at: response.ticket.synced_at,
        })
        toast.success('Ticket status synced')
      } else {
        toast.error(response.error || 'Failed to sync ticket')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to sync ticket')
    } finally {
      setSyncing(false)
    }
  }

  const handleUnlinkTicket = async () => {
    try {
      const response = await api.unlinkJiraTicket(vulnId)
      if (response.success) {
        setTicketInfo({ key: null, url: null, status: null, synced_at: null })
        toast.success('Jira ticket unlinked')
      } else {
        toast.error(response.message || 'Failed to unlink ticket')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to unlink ticket')
    }
  }

  // Don't render if Jira is not configured
  if (jiraConfigured === false) {
    return null
  }

  // Loading state
  if (jiraConfigured === null) {
    return (
      <div className="pt-3 border-t border-gray-200">
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking Jira integration...
        </div>
      </div>
    )
  }

  const statusColors: Record<string, string> = {
    'Open': 'bg-blue-100 text-blue-700',
    'In Progress': 'bg-yellow-100 text-yellow-700',
    'Done': 'bg-green-100 text-green-700',
    'Closed': 'bg-gray-100 text-gray-700',
    'Resolved': 'bg-green-100 text-green-700',
    'Deleted': 'bg-red-100 text-red-700',
  }

  return (
    <div className="pt-3 border-t border-gray-200">
      <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
        Jira Ticket
      </h5>

      {ticketInfo.key ? (
        <div className="space-y-3">
          {/* Ticket Info Display */}
          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg">
                <Ticket className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <a
                  href={ticketInfo.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {ticketInfo.key}
                  <ExternalLink className="h-3 w-3" />
                </a>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    statusColors[ticketInfo.status || ''] || 'bg-gray-100 text-gray-700'
                  }`}>
                    {ticketInfo.status || 'Unknown'}
                  </span>
                  {ticketInfo.synced_at && (
                    <span className="text-xs text-gray-400">
                      Synced {formatDate(ticketInfo.synced_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleSyncTicket()
                }}
                disabled={syncing}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                title="Sync ticket status"
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleUnlinkTicket()
                }}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Unlink ticket"
              >
                <Unlink className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleCreateTicket()
            }}
            disabled={creating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Ticket className="h-4 w-4" />
            )}
            Create Jira Ticket
          </button>
          <span className="text-xs text-gray-400">
            Automatically maps severity to priority
          </span>
        </div>
      )}
    </div>
  )
}

export default function Scanners() {
  const { formatDate, formatDateTime } = useDatePreferences()
  const [activeTab, setActiveTab] = useState<'configs' | 'results'>('configs')
  const [configs, setConfigs] = useState<ScanConfig[]>([])
  const [results, setResults] = useState<ScanResult[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState<ScanConfig | null>(null)
  const [selectedResult, setSelectedResult] = useState<ScanResult | null>(null)
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>([])
  // POC Validation state
  const [pocCredits, setPocCredits] = useState<PocCredits | null>(null)
  const [validatingVulnId, setValidatingVulnId] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  // Sort and filter state for results table (#118)
  const [resultsSortField, setResultsSortField] = useState<'started_at' | 'severity' | 'vulnerabilities_found'>('started_at')
  const [resultsSortOrder, setResultsSortOrder] = useState<'asc' | 'desc'>('desc')
  const [resultsStatusFilter, setResultsStatusFilter] = useState<string | null>(null)
  const [resultsSeverityFilter, setResultsSeverityFilter] = useState<string | null>(null)
  // Cancel scan state (#66)
  const [cancellingScans, setCancellingScans] = useState<Set<string>>(new Set())
  // Scan comparison state (#101)
  const [showCompareModal, setShowCompareModal] = useState(false)
  const [compareSelection, setCompareSelection] = useState<{ scan1: string | null; scan2: string | null }>({ scan1: null, scan2: null })
  const [comparisonResult, setComparisonResult] = useState<any>(null)
  const [comparing, setComparing] = useState(false)
  // Scan mode selection state (Issue #261)
  const [showRunScanModal, setShowRunScanModal] = useState(false)
  const [runScanConfigId, setRunScanConfigId] = useState<string | null>(null)
  const [selectedScanMode, setSelectedScanMode] = useState<'quick' | 'full' | 'stealth'>('quick')
  const [startingScan, setStartingScan] = useState(false)
  // Users list for remediation assignment (#122)
  const [users, setUsers] = useState<Array<{ id: string; username: string; first_name?: string; last_name?: string }>>([])
  // Methodology modal state
  const [showMethodologyModal, setShowMethodologyModal] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  // Pagination state
  const [resultsPagination, setResultsPagination] = useState<Pagination>({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false,
  })
  const [configsPagination, setConfigsPagination] = useState<Pagination>({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  })
  const RESULTS_PAGE_SIZE = 20
  const CONFIGS_PAGE_SIZE = 50

  useEffect(() => {
    loadData()
  }, [])


  const loadData = async (resultsOffset = 0, configsOffset = 0) => {
    setLoading(true)
    try {
      const [configsRes, resultsRes, statsRes, creditsRes, usersRes] = await Promise.all([
        api.getScanConfigs({ limit: CONFIGS_PAGE_SIZE, offset: configsOffset }),
        api.getScanResults({ limit: RESULTS_PAGE_SIZE, offset: resultsOffset }),
        api.getScannerStats(),
        api.getPocCredits().catch(() => ({ success: false as const, balance: 0, updated_at: 0 })),
        api.getUsers().catch(() => ({ success: false as const, users: [] })),
      ])

      if (configsRes.success) {
        setConfigs(configsRes.configs)
        setConfigsPagination(configsRes.pagination)
      }
      if (resultsRes.success) {
        setResults(resultsRes.results)
        setResultsPagination(resultsRes.pagination)
      }
      if (statsRes.success) setStats(statsRes.stats)
      if (creditsRes.success) setPocCredits({ balance: creditsRes.balance, updated_at: creditsRes.updated_at })
      if (usersRes.success) setUsers(usersRes.users)
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const [configsRes, resultsRes, statsRes] = await Promise.all([
        api.getScanConfigs({ limit: CONFIGS_PAGE_SIZE, offset: configsPagination.offset }),
        api.getScanResults({ limit: RESULTS_PAGE_SIZE, offset: resultsPagination.offset }),
        api.getScannerStats(),
      ])

      if (configsRes.success) {
        setConfigs(configsRes.configs)
        setConfigsPagination(configsRes.pagination)
      }
      if (resultsRes.success) {
        setResults(resultsRes.results)
        setResultsPagination(resultsRes.pagination)
      }
      if (statsRes.success) setStats(statsRes.stats)
    } catch (error) {
      console.error('Failed to refresh data:', error)
    } finally {
      setRefreshing(false)
    }
  }

  // Pagination handlers
  const handleResultsPageChange = async (newOffset: number) => {
    setRefreshing(true)
    try {
      const resultsRes = await api.getScanResults({ limit: RESULTS_PAGE_SIZE, offset: newOffset })
      if (resultsRes.success) {
        setResults(resultsRes.results)
        setResultsPagination(resultsRes.pagination)
      }
    } catch (error) {
      console.error('Failed to load page:', error)
    } finally {
      setRefreshing(false)
    }
  }

  const handleConfigsPageChange = async (newOffset: number) => {
    setRefreshing(true)
    try {
      const configsRes = await api.getScanConfigs({ limit: CONFIGS_PAGE_SIZE, offset: newOffset })
      if (configsRes.success) {
        setConfigs(configsRes.configs)
        setConfigsPagination(configsRes.pagination)
      }
    } catch (error) {
      console.error('Failed to load page:', error)
    } finally {
      setRefreshing(false)
    }
  }

  const currentResultsPage = Math.floor(resultsPagination.offset / RESULTS_PAGE_SIZE) + 1
  const totalResultsPages = Math.ceil(resultsPagination.total / RESULTS_PAGE_SIZE)
  const currentConfigsPage = Math.floor(configsPagination.offset / CONFIGS_PAGE_SIZE) + 1
  const totalConfigsPages = Math.ceil(configsPagination.total / CONFIGS_PAGE_SIZE)

  const handleValidatePoc = async (vulnId: string) => {
    const confirmed = await confirm({
      title: 'Validate POC',
      message: 'This will use 1 credit ($29.99) to validate exploitability. Continue?',
      type: 'warning',
      confirmText: 'Validate',
    })
    if (!confirmed) return

    setValidatingVulnId(vulnId)
    try {
      const response = await api.validatePoc(vulnId)
      if (response.success) {
        toast.success(`POC validation queued! Credits remaining: ${response.credits_remaining}`)
        // Refresh credits
        const creditsRes = await api.getPocCredits()
        if (creditsRes.success) {
          setPocCredits({ balance: creditsRes.balance, updated_at: creditsRes.updated_at })
        }
        // Update vulnerability status to show it's being validated
        setVulnerabilities(prev => prev.map(v =>
          v.id === vulnId ? { ...v, exploitability_status: 'validating' as const } : v
        ))
      } else {
        toast.error(response.error || 'Failed to initiate POC validation')
      }
    } catch (error) {
      console.error('Failed to validate POC:', error)
      toast.error('Failed to initiate POC validation')
    } finally {
      setValidatingVulnId(null)
    }
  }

  // Open run scan modal with mode selection (Issue #261)
  const handleRunScan = (configId: string) => {
    setRunScanConfigId(configId)
    // Use config's default scan mode if set, otherwise 'quick'
    const config = configs.find(c => c.id === configId)
    let defaultMode: 'quick' | 'full' | 'stealth' = 'quick'
    if (config?.scan_options) {
      const options = typeof config.scan_options === 'string'
        ? JSON.parse(config.scan_options)
        : config.scan_options
      defaultMode = options?.scan_mode || 'quick'
    }
    setSelectedScanMode(defaultMode)
    setShowRunScanModal(true)
  }

  // Actually run the scan with selected mode
  const executeRunScan = async () => {
    if (!runScanConfigId) return
    setStartingScan(true)
    try {
      const response = await api.runScan(runScanConfigId, { scan_mode: selectedScanMode })
      if (response.success) {
        toast.success(`${SCAN_MODES.find(m => m.id === selectedScanMode)?.name || 'Scan'} queued successfully!`)
        setShowRunScanModal(false)
        loadData()
      }
    } catch (error) {
      console.error('Failed to run scan:', error)
      toast.error('Failed to queue scan')
    } finally {
      setStartingScan(false)
    }
  }

  const handleDeleteConfig = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Scan Configuration',
      message: 'Are you sure you want to delete this scan configuration? All associated results will also be deleted.',
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return

    try {
      await api.deleteScanConfig(id)
      toast.success('Configuration deleted successfully')
      loadData()
    } catch (error) {
      console.error('Failed to delete config:', error)
      toast.error('Failed to delete configuration')
    }
  }

  const handleDeleteResult = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Scan Result',
      message: 'Are you sure you want to delete this scan result?',
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return

    try {
      await api.deleteScanResult(id)
      toast.success('Scan result deleted successfully')
      loadData()
    } catch (error) {
      console.error('Failed to delete result:', error)
      toast.error('Failed to delete scan result')
    }
  }

  // Cancel scan handler (#66)
  const handleCancelScan = async (id: string) => {
    const confirmed = await confirm({
      title: 'Cancel Scan',
      message: 'Are you sure you want to cancel this scan?',
      type: 'warning',
      confirmText: 'Cancel Scan',
    })
    if (!confirmed) return

    setCancellingScans(prev => new Set(prev).add(id))
    try {
      const response = await api.cancelScan(id)
      if (response.success) {
        toast.success('Scan cancelled successfully')
        // Update the scan status in the local state
        setResults(prev => prev.map(r =>
          r.id === id ? { ...r, status: 'cancelled' } : r
        ))
      } else {
        toast.error(response.error || 'Failed to cancel scan')
      }
    } catch (error) {
      console.error('Failed to cancel scan:', error)
      toast.error('Failed to cancel scan')
    } finally {
      setCancellingScans(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  const handleCleanupQueued = async () => {
    const confirmed = await confirm({
      title: 'Clear Queued Scans',
      message: 'Delete all queued scans that never completed?',
      type: 'warning',
      confirmText: 'Clear',
    })
    if (!confirmed) return

    try {
      const response = await api.cleanupScanResults({ status: 'queued' })
      if (response.success) {
        toast.success(response.message)
        loadData()
      }
    } catch (error) {
      console.error('Failed to cleanup:', error)
      toast.error('Failed to cleanup scan results')
    }
  }

  const handleViewResult = async (result: ScanResult) => {
    setSelectedResult(result)
    setShowResultModal(true)

    try {
      const response = await api.getScanResult(result.id)
      if (response.success) {
        // Map API response to include POC fields with defaults
        setVulnerabilities(response.vulnerabilities.map(v => ({
          ...v,
          exploitability_status: (v as any).exploitability_status || null,
          last_poc_validation_id: (v as any).last_poc_validation_id || null,
          poc_validated_at: (v as any).poc_validated_at || null,
        })))
      }
    } catch (error) {
      console.error('Failed to load vulnerabilities:', error)
    }
  }

  const handleMarkFalsePositive = async (vulnId: string, isFalsePositive: boolean, reason?: string) => {
    try {
      await api.markVulnerabilityFalsePositive(vulnId, {
        is_false_positive: isFalsePositive,
        false_positive_reason: reason,
      })
      // Reload vulnerabilities
      if (selectedResult) {
        const response = await api.getScanResult(selectedResult.id)
        if (response.success) {
          // Map API response to include POC fields with defaults
          setVulnerabilities(response.vulnerabilities.map(v => ({
            ...v,
            exploitability_status: (v as any).exploitability_status || null,
            last_poc_validation_id: (v as any).last_poc_validation_id || null,
            poc_validated_at: (v as any).poc_validated_at || null,
          })))
        }
      }
    } catch (error) {
      console.error('Failed to update vulnerability:', error)
    }
  }

  // Remediation tracking handler (#122)
  const handleUpdateRemediation = async (vulnId: string, data: {
    status?: 'new' | 'acknowledged' | 'in_progress' | 'remediated' | 'accepted_risk' | 'false_positive' | 'deferred'
    assigned_to?: string | null
    due_date?: number | null
    notes?: string | null
  }) => {
    await api.updateVulnerabilityRemediation(vulnId, data)
    // Reload vulnerabilities to show updated status
    if (selectedResult) {
      const response = await api.getScanResult(selectedResult.id)
      if (response.success) {
        setVulnerabilities(response.vulnerabilities.map(v => ({
          ...v,
          exploitability_status: (v as any).exploitability_status || null,
          last_poc_validation_id: (v as any).last_poc_validation_id || null,
          poc_validated_at: (v as any).poc_validated_at || null,
        })))
      }
    }
  }

  const formatScanDate = (ts: number | null) => {
    if (!ts) return 'Never'
    return formatDateTime(ts)
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }

  // Format elapsed time since scan started (#58)
  const formatElapsedTime = (startedAt: number) => {
    const elapsed = Date.now() - startedAt
    const minutes = Math.floor(elapsed / 60000)
    if (minutes < 1) return 'just started'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ${minutes % 60}m ago`
    return `${Math.floor(hours / 24)}d ${hours % 24}h ago`
  }

  // Compute sorted and filtered results (#118)
  const sortedAndFilteredResults = (() => {
    let filtered = [...results]

    // Apply status filter
    if (resultsStatusFilter) {
      filtered = filtered.filter(r => r.status === resultsStatusFilter)
    }

    // Apply severity filter (only show results that have at least one vulnerability of this severity)
    if (resultsSeverityFilter) {
      filtered = filtered.filter(r => {
        if (resultsSeverityFilter === 'critical') return r.critical_count > 0
        if (resultsSeverityFilter === 'high') return r.high_count > 0
        if (resultsSeverityFilter === 'medium') return r.medium_count > 0
        if (resultsSeverityFilter === 'low') return r.low_count > 0
        return true
      })
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0
      if (resultsSortField === 'started_at') {
        comparison = (a.started_at || 0) - (b.started_at || 0)
      } else if (resultsSortField === 'vulnerabilities_found') {
        comparison = (a.vulnerabilities_found || 0) - (b.vulnerabilities_found || 0)
      } else if (resultsSortField === 'severity') {
        // Sort by severity: critical > high > medium > low
        const severityScore = (r: ScanResult) =>
          r.critical_count * 1000 + r.high_count * 100 + r.medium_count * 10 + r.low_count
        comparison = severityScore(a) - severityScore(b)
      }
      return resultsSortOrder === 'asc' ? comparison : -comparison
    })

    return filtered
  })()

  const toggleSort = (field: 'started_at' | 'severity' | 'vulnerabilities_found') => {
    if (resultsSortField === field) {
      setResultsSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setResultsSortField(field)
      setResultsSortOrder('desc')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vulnerability Scanner</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage scan configurations and view vulnerability scan results
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowMethodologyModal(true)}
            className="inline-flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="View scanning methodology documentation"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Methodology
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => {
              setSelectedConfig(null)
              setShowConfigModal(true)
            }}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Scan Config
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
              <Settings className="h-6 w-6 text-gray-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Configs</p>
                <p className="text-xl font-bold text-gray-900">{stats.configs_count}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Scans (30d)</p>
                <p className="text-xl font-bold text-gray-900">{stats.recent_scans.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-red-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Critical</p>
                <p className="text-xl font-bold text-red-600">{stats.recent_scans.total_critical}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-6 w-6 text-orange-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">High</p>
                <p className="text-xl font-bold text-orange-600">{stats.recent_scans.total_high}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-6 w-6 text-yellow-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Medium</p>
                <p className="text-xl font-bold text-yellow-600">{stats.recent_scans.total_medium}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('configs')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'configs'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Configurations ({configsPagination.total > 0 ? configsPagination.total : configs.length})
          </button>
          <button
            onClick={() => setActiveTab('results')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'results'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Results ({resultsPagination.total > 0 ? resultsPagination.total : results.length})
          </button>
        </nav>
      </div>

      {/* Configurations Tab */}
      {activeTab === 'configs' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {configs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Shield className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p>No scan configurations yet</p>
                <p className="text-sm mt-1">Create a configuration to start scanning</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {configs.map((config) => (
                    <tr key={config.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{config.name}</p>
                          {config.description && (
                            <p className="text-sm text-gray-500">{config.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {JSON.parse(config.targets).length} target(s)
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const mode = getScanModeFromOptions(config.scan_options)
                          const badge = SCAN_MODE_BADGES[mode]
                          return (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
                              {badge.icon} {badge.label}
                            </span>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 capitalize">{config.frequency}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(config.last_run_at)}</td>
                      <td className="px-6 py-4">
                        {config.last_scan_status ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[config.last_scan_status]}`}>
                            {config.last_scan_status}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleRunScan(config.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                            title="Run scan now"
                          >
                            <Play className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedConfig(config)
                              setShowConfigModal(true)
                            }}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                            title="Edit"
                          >
                            <Settings className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteConfig(config.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {/* Pagination Controls for Configs */}
          {configsPagination.total > CONFIGS_PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-600">
                Showing {configsPagination.offset + 1} - {Math.min(configsPagination.offset + configs.length, configsPagination.total)} of {configsPagination.total} configurations
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleConfigsPageChange(configsPagination.offset - CONFIGS_PAGE_SIZE)}
                  disabled={configsPagination.offset === 0 || refreshing}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {/* Page numbers */}
                  {(() => {
                    const pages: (number | 'ellipsis')[] = []
                    const totalPages = totalConfigsPages
                    const currentPage = currentConfigsPage

                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i)
                      }
                    } else {
                      pages.push(1)
                      if (currentPage > 3) {
                        pages.push('ellipsis')
                      }
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                        pages.push(i)
                      }
                      if (currentPage < totalPages - 2) {
                        pages.push('ellipsis')
                      }
                      pages.push(totalPages)
                    }

                    return pages.map((page, idx) => {
                      if (page === 'ellipsis') {
                        return (
                          <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-400">
                            ...
                          </span>
                        )
                      }
                      const isActive = page === currentPage
                      return (
                        <button
                          key={page}
                          onClick={() => handleConfigsPageChange((page - 1) * CONFIGS_PAGE_SIZE)}
                          disabled={refreshing}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            isActive
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          } disabled:opacity-50`}
                        >
                          {page}
                        </button>
                      )
                    })
                  })()}
                </div>
                <button
                  onClick={() => handleConfigsPageChange(configsPagination.offset + CONFIGS_PAGE_SIZE)}
                  disabled={!configsPagination.hasMore || refreshing}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && (
        <div className="space-y-4">
          {/* Filter and Sort Controls (#118) */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={resultsStatusFilter || ''}
                onChange={(e) => setResultsStatusFilter(e.target.value || null)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Statuses</option>
                <option value="queued">Queued</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Severity Filter */}
            <select
              value={resultsSeverityFilter || ''}
              onChange={(e) => setResultsSeverityFilter(e.target.value || null)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Severities</option>
              <option value="critical">Has Critical</option>
              <option value="high">Has High</option>
              <option value="medium">Has Medium</option>
              <option value="low">Has Low</option>
            </select>

            {/* Clear Filters */}
            {(resultsStatusFilter || resultsSeverityFilter) && (
              <button
                onClick={() => {
                  setResultsStatusFilter(null)
                  setResultsSeverityFilter(null)
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear filters
              </button>
            )}

            <div className="flex-1" />

            {/* Compare Scans Button (#101) */}
            {results.filter(r => r.status === 'completed').length >= 2 && (
              <button
                onClick={() => setShowCompareModal(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"
              >
                <GitCompare className="h-4 w-4 mr-1.5" />
                Compare Scans
              </button>
            )}

            {/* Cleanup Actions */}
            {results.some(r => r.status === 'queued') && (
              <button
                onClick={handleCleanupQueued}
                className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Clear Queued Scans
              </button>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {results.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p>No scan results yet</p>
              <p className="text-sm mt-1">Run a scan to see results</p>
            </div>
          ) : sortedAndFilteredResults.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Filter className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p>No results match the current filters</p>
              <button
                onClick={() => {
                  setResultsStatusFilter(null)
                  setResultsSeverityFilter(null)
                }}
                className="mt-2 text-sm text-primary-600 hover:text-primary-700 underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('started_at')}
                  >
                    <span className="flex items-center gap-1">
                      Started
                      <ArrowUpDown className={`h-3 w-3 ${resultsSortField === 'started_at' ? 'text-primary-600' : 'text-gray-400'}`} />
                    </span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('severity')}
                  >
                    <span className="flex items-center gap-1">
                      Findings
                      <ArrowUpDown className={`h-3 w-3 ${resultsSortField === 'severity' ? 'text-primary-600' : 'text-gray-400'}`} />
                    </span>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedAndFilteredResults.map((result) => (
                  <tr key={result.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{result.config_name}</p>
                      <p className="text-sm text-gray-500 capitalize">{result.scan_type}</p>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        // Look up config to get scan_mode
                        const config = configs.find(c => c.id === result.scan_config_id)
                        const mode = config ? getScanModeFromOptions(config.scan_options) : 'quick'
                        const badge = SCAN_MODE_BADGES[mode]
                        return (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
                            {badge.icon} {badge.label}
                          </span>
                        )
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(result.started_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDuration(result.duration)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[result.status]} inline-block w-fit`}>
                          {result.status}
                        </span>
                        {/* Show current phase for running scans (Issue #261 - detailed nuclei phases) */}
                        {result.status === 'running' && result.current_phase && (
                          <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {result.current_phase === 'recon' && 'Reconnaissance...'}
                            {result.current_phase === 'ports' && 'Port Discovery...'}
                            {result.current_phase === 'discovery' && 'Content Discovery...'}
                            {result.current_phase === 'vulns' && 'Vulnerability Scan...'}
                            {/* Handle detailed nuclei phase format: "4A: CVE Scanning (1/10)" */}
                            {result.current_phase.startsWith('4') && result.current_phase}
                          </span>
                        )}
                        {/* Show elapsed time for running/queued scans (#58) */}
                        {(result.status === 'running' || result.status === 'queued') && result.started_at && (
                          <span className="text-xs text-gray-400">
                            {formatElapsedTime(result.started_at)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {result.status === 'completed' && (
                        <div className="flex items-center gap-1">
                          {result.critical_count > 0 && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                              {result.critical_count} crit
                            </span>
                          )}
                          {result.high_count > 0 && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded">
                              {result.high_count} high
                            </span>
                          )}
                          {result.medium_count > 0 && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded">
                              {result.medium_count} med
                            </span>
                          )}
                          {result.low_count > 0 && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                              {result.low_count} low
                            </span>
                          )}
                          {result.info_count > 0 && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                              {result.info_count} info
                            </span>
                          )}
                          {result.vulnerabilities_found === 0 && (
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <CheckCircle className="h-4 w-4" />
                              Clean
                            </span>
                          )}
                        </div>
                      )}
                      {result.status === 'failed' && (
                        <span className="text-sm text-red-500">{result.error_message || 'Error'}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Cancel button for running/queued scans (#66) */}
                        {(result.status === 'running' || result.status === 'queued') && (
                          <button
                            onClick={() => handleCancelScan(result.id)}
                            disabled={cancellingScans.has(result.id)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded hover:bg-orange-100 disabled:opacity-50"
                            title="Cancel scan"
                          >
                            {cancellingScans.has(result.id) ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <StopCircle className="h-3 w-3 mr-1" />
                            )}
                            Cancel
                          </button>
                        )}
                        <button
                          onClick={() => handleViewResult(result)}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleDeleteResult(result.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete result"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
          </div>

          {/* Pagination Controls */}
          {resultsPagination.total > RESULTS_PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-600">
                Showing {resultsPagination.offset + 1} - {Math.min(resultsPagination.offset + results.length, resultsPagination.total)} of {resultsPagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleResultsPageChange(resultsPagination.offset - RESULTS_PAGE_SIZE)}
                  disabled={resultsPagination.offset === 0 || refreshing}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {/* Page numbers */}
                  {(() => {
                    const pages: (number | 'ellipsis')[] = []
                    const totalPages = totalResultsPages
                    const currentPage = currentResultsPage

                    if (totalPages <= 7) {
                      // Show all pages if 7 or fewer
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i)
                      }
                    } else {
                      // Show first page, last page, and pages around current
                      pages.push(1)
                      if (currentPage > 3) {
                        pages.push('ellipsis')
                      }
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                        pages.push(i)
                      }
                      if (currentPage < totalPages - 2) {
                        pages.push('ellipsis')
                      }
                      pages.push(totalPages)
                    }

                    return pages.map((page, idx) => {
                      if (page === 'ellipsis') {
                        return (
                          <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-400">
                            ...
                          </span>
                        )
                      }
                      const isActive = page === currentPage
                      return (
                        <button
                          key={page}
                          onClick={() => handleResultsPageChange((page - 1) * RESULTS_PAGE_SIZE)}
                          disabled={refreshing}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            isActive
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          } disabled:opacity-50`}
                        >
                          {page}
                        </button>
                      )
                    })
                  })()}
                </div>
                <button
                  onClick={() => handleResultsPageChange(resultsPagination.offset + RESULTS_PAGE_SIZE)}
                  disabled={!resultsPagination.hasMore || refreshing}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <ConfigModal
          config={selectedConfig}
          onClose={() => setShowConfigModal(false)}
          onSave={() => {
            setShowConfigModal(false)
            loadData()
          }}
        />
      )}

      {/* Result Detail Modal */}
      {showResultModal && selectedResult && (
        <ResultModal
          result={selectedResult}
          vulnerabilities={vulnerabilities}
          onClose={() => {
            setShowResultModal(false)
            setSelectedResult(null)
            setVulnerabilities([])
          }}
          onMarkFalsePositive={handleMarkFalsePositive}
          onValidatePoc={handleValidatePoc}
          onUpdateRemediation={handleUpdateRemediation}
          pocCredits={pocCredits}
          validatingVulnId={validatingVulnId}
          users={users}
        />
      )}

      {/* Compare Scans Modal (#101) */}
      {showCompareModal && (
        <CompareModal
          results={results}
          onClose={() => setShowCompareModal(false)}
        />
      )}

      {/* Run Scan Modal with Mode Selection (Issue #261) */}
      {showRunScanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Play className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Run Vulnerability Scan</h2>
                  <p className="text-sm text-gray-500">Select scan mode based on your requirements</p>
                </div>
              </div>
              <button
                onClick={() => setShowRunScanModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {SCAN_MODES.map((mode) => {
                  const Icon = mode.icon
                  const isSelected = selectedScanMode === mode.id
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setSelectedScanMode(mode.id as 'quick' | 'full' | 'stealth')}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                              {mode.name}
                            </h3>
                            {isSelected && (
                              <CheckCircle className="w-4 h-4 text-blue-500" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{mode.description}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {mode.details.map((detail, idx) => (
                              <span
                                key={idx}
                                className={`text-xs px-2 py-1 rounded ${
                                  isSelected
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {detail}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Category Legend */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Vulnerability Categories Detected</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {Object.entries(VULN_CATEGORIES).slice(0, 10).map(([key, cat]) => (
                    <div key={key} className="flex items-center gap-1.5" title={cat.description}>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${cat.color}`}>
                        {cat.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setShowRunScanModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={startingScan}
              >
                Cancel
              </button>
              <button
                onClick={executeRunScan}
                disabled={startingScan}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {startingScan ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Start {SCAN_MODES.find(m => m.id === selectedScanMode)?.name}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanner Methodology Modal */}
      <ScannerMethodologyModal
        isOpen={showMethodologyModal}
        onClose={() => setShowMethodologyModal(false)}
      />
    </div>
  )
}

// Scan Profile type for #181
type ScanProfile = {
  id: string
  name: string
  description: string | null
  framework_id: string | null
  framework_name: string | null
  profile_type: 'system' | 'custom'
  scan_settings: Record<string, any>
  nuclei_templates: string[]
  nuclei_severity: string | null
  nmap_scripts: string[]
  port_range: string | null
  enabled: number
  linked_framework_name: string | null
  linked_framework_display_name: string | null
  created_at: number
  updated_at: number
}

// Config Modal Component
function ConfigModal({
  config,
  onClose,
  onSave,
}: {
  config: ScanConfig | null
  onClose: () => void
  onSave: () => void
}) {
  const [name, setName] = useState(config?.name || '')
  const [description, setDescription] = useState(config?.description || '')
  const [targets, setTargets] = useState(config ? JSON.parse(config.targets).join('\n') : '')
  const [frequency, setFrequency] = useState(config?.frequency || 'weekly')
  const [enabled, setEnabled] = useState(config?.enabled !== 0)
  const [saving, setSaving] = useState(false)
  // Scan Mode state (Issue #261) - parse JSON if needed
  const [scanMode, setScanMode] = useState<'quick' | 'full' | 'stealth'>(() => {
    if (!config?.scan_options) return 'quick'
    const options = typeof config.scan_options === 'string'
      ? JSON.parse(config.scan_options)
      : config.scan_options
    return options?.scan_mode || 'quick'
  })
  // Schedule settings (#310)
  const [scheduleHour, setScheduleHour] = useState((config as any)?.schedule_hour ?? 2)
  const [scheduleDay, setScheduleDay] = useState((config as any)?.schedule_day ?? 1)
  const toast = useToast()

  const handleSave = async () => {
    if (!name.trim() || !targets.trim()) {
      toast.warning('Name and targets are required')
      return
    }

    setSaving(true)
    try {
      const targetsList = targets.split('\n').map((t: string) => t.trim()).filter(Boolean)

      if (config) {
        await api.updateScanConfig(config.id, {
          name,
          description,
          targets: targetsList,
          frequency,
          enabled,
          scan_options: { scan_mode: scanMode },
          schedule_hour: scheduleHour,
          schedule_day: scheduleDay,
        })
      } else {
        await api.createScanConfig({
          name,
          description,
          targets: targetsList,
          frequency,
          enabled,
          scan_options: { scan_mode: scanMode },
          schedule_hour: scheduleHour,
          schedule_day: scheduleDay,
        })
      }
      toast.success(`Configuration ${config ? 'updated' : 'created'} successfully`)
      onSave()
    } catch (error) {
      console.error('Failed to save config:', error)
      toast.error('Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {config ? 'Edit Scan Configuration' : 'New Scan Configuration'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              placeholder="Production Network Scan"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              placeholder="Weekly vulnerability scan of production servers"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="manual">Manual Only</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {/* Scan Mode Selection (Issue #261) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Scan Mode</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'quick', name: 'Quick', desc: 'Fast scan, critical/high vulnerabilities only', icon: '⚡' },
                { id: 'full', name: 'Full', desc: 'Comprehensive scan, all severity levels', icon: '🛡️' },
                { id: 'stealth', name: 'Stealth', desc: 'Rate-limited, low-footprint scan', icon: '🎯' },
              ].map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setScanMode(mode.id as 'quick' | 'full' | 'stealth')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    scanMode === mode.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{mode.icon}</span>
                    <span className={`font-medium ${scanMode === mode.id ? 'text-primary-700' : 'text-gray-900'}`}>
                      {mode.name}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{mode.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Targets * <span className="text-gray-400 font-normal">(one per line)</span>
            </label>
            <textarea
              value={targets}
              onChange={(e) => setTargets(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              rows={4}
              placeholder="192.168.1.0/24&#10;app.example.com&#10;10.0.1.50"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
              Enable scheduled scanning
            </label>
          </div>

          {/* Schedule Settings (#310) - shown only when enabled and not manual */}
          {enabled && frequency !== 'manual' && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Schedule Settings
              </label>
              <div className="grid grid-cols-2 gap-4">
                {/* Time picker */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Time (UTC)</label>
                  <select
                    value={scheduleHour}
                    onChange={(e) => setScheduleHour(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i.toString().padStart(2, '0')}:00 UTC
                      </option>
                    ))}
                  </select>
                </div>

                {/* Day picker - varies by frequency */}
                {frequency === 'weekly' && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Day of Week</label>
                    <select
                      value={scheduleDay}
                      onChange={(e) => setScheduleDay(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value={0}>Sunday</option>
                      <option value={1}>Monday</option>
                      <option value={2}>Tuesday</option>
                      <option value={3}>Wednesday</option>
                      <option value={4}>Thursday</option>
                      <option value={5}>Friday</option>
                      <option value={6}>Saturday</option>
                    </select>
                  </div>
                )}

                {frequency === 'monthly' && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Day of Month</label>
                    <select
                      value={scheduleDay}
                      onChange={(e) => setScheduleDay(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {Array.from({ length: 28 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Helper to format vulnerability names from raw IDs
function formatVulnName(vuln: Vulnerability): string {
  const name = vuln.vulnerability_name

  // If it's a CVE, keep it but also show the service context
  if (name.startsWith('CVE-')) {
    return name
  }

  // HTTPD: prefix - show as HTTP Server Vulnerability
  if (name.startsWith('HTTPD:')) {
    return 'HTTP Server Vulnerability'
  }

  // PACKETSTORM: prefix - show as Packet Storm Advisory
  if (name.startsWith('PACKETSTORM:')) {
    return 'Packet Storm Security Advisory'
  }

  // EDB-ID: prefix - show as Exploit DB Reference
  if (name.startsWith('EDB-ID:')) {
    return 'Exploit Database Reference'
  }

  // SSV: prefix - show as Security Vulnerability
  if (name.startsWith('SSV:')) {
    return 'Security Vulnerability'
  }

  // MSF: prefix - show as Metasploit Module
  if (name.startsWith('MSF:')) {
    return 'Metasploit Module Available'
  }

  // 1337DAY-ID: prefix
  if (name.startsWith('1337DAY-ID:')) {
    return 'Exploit Available (1337day)'
  }

  // Generic hex-like ID
  if (/^[A-F0-9]{8,}$/i.test(name.replace(/[:-]/g, ''))) {
    return vuln.service ? `${vuln.service.toUpperCase()} Vulnerability` : 'Detected Vulnerability'
  }

  return name
}

// Helper to get vulnerability category/type (Issue #261)
function getVulnCategory(vuln: Vulnerability): { key: string; label: string; color: string } {
  // Use scanner-provided category if available
  if (vuln.category && VULN_CATEGORIES[vuln.category]) {
    const cat = VULN_CATEGORIES[vuln.category]
    return { key: vuln.category, label: cat.label, color: cat.color }
  }

  // Fallback: infer category from vulnerability attributes
  const name = vuln.vulnerability_name
  const id = vuln.vulnerability_id || ''

  if (name.startsWith('CVE-') || id.startsWith('CVE-')) {
    return { key: 'cve', label: 'CVE', color: VULN_CATEGORIES.cve.color }
  }
  if (vuln.service === 'ssl' || vuln.service === 'https' || name.toLowerCase().includes('ssl') || name.toLowerCase().includes('tls')) {
    return { key: 'ssl', label: 'SSL/TLS', color: VULN_CATEGORIES.ssl.color }
  }
  if (name.toLowerCase().includes('injection') || name.toLowerCase().includes('xss') || name.toLowerCase().includes('sqli')) {
    return { key: 'injection', label: 'Injection', color: VULN_CATEGORIES.injection.color }
  }
  if (name.toLowerCase().includes('auth') || name.toLowerCase().includes('credential') || name.toLowerCase().includes('default')) {
    return { key: 'auth', label: 'Auth', color: VULN_CATEGORIES.auth.color }
  }
  if (name.toLowerCase().includes('exposure') || name.toLowerCase().includes('disclosure') || name.toLowerCase().includes('leak')) {
    return { key: 'exposure', label: 'Exposure', color: VULN_CATEGORIES.exposure.color }
  }
  if (name.toLowerCase().includes('misconfig') || name.toLowerCase().includes('configuration')) {
    return { key: 'misconfig', label: 'Misconfig', color: VULN_CATEGORIES.misconfig.color }
  }
  if (vuln.service === 'ssh' || vuln.service === 'ftp' || vuln.service === 'smtp' || vuln.service === 'dns') {
    return { key: 'network', label: 'Network', color: VULN_CATEGORIES.network.color }
  }

  return { key: 'http', label: 'HTTP', color: VULN_CATEGORIES.http.color }
}

// Calculate risk score (0-100) using industry-standard CVSS-based methodology
// Based on FAIR (Factor Analysis of Information Risk) and OWASP Risk Rating
// Info findings are EXCLUDED - they are informational, not vulnerabilities
function calculateRiskScore(result: ScanResult): number {
  const { critical_count, high_count, medium_count, low_count } = result

  // If no actual vulnerabilities (excluding info), risk is 0
  const totalVulns = critical_count + high_count + medium_count + low_count
  if (totalVulns === 0) return 0

  // CVSS-aligned base severity weights
  // Critical (CVSS 9.0-10.0): Base 40 points
  // High (CVSS 7.0-8.9): Base 20 points
  // Medium (CVSS 4.0-6.9): Base 8 points
  // Low (CVSS 0.1-3.9): Base 2 points
  // Info: 0 points (informational, not actual vulnerabilities)

  // Logarithmic diminishing returns for multiple findings of same severity
  // Prevents a scan with 100 lows from having same score as one with 5 criticals
  const calcSeverityScore = (count: number, baseWeight: number) => {
    if (count === 0) return 0
    // First finding = full weight, subsequent findings have diminishing impact
    // Formula: baseWeight * log2(count + 1) / log2(2) normalized
    // This gives: 1 vuln = 100%, 2 = 158%, 4 = 200%, 10 = 332% of base
    return baseWeight * (1 + Math.log2(count))
  }

  const criticalScore = calcSeverityScore(critical_count, 40)
  const highScore = calcSeverityScore(high_count, 20)
  const mediumScore = calcSeverityScore(medium_count, 8)
  const lowScore = calcSeverityScore(low_count, 2)

  // Maximum achievable score based on highest severity present
  // This prevents low-severity-only scans from reaching "Critical" risk
  let maxPossible: number
  if (critical_count > 0) {
    maxPossible = 100  // Critical vulns can reach maximum risk
  } else if (high_count > 0) {
    maxPossible = 79   // High vulns cap at High risk
  } else if (medium_count > 0) {
    maxPossible = 59   // Medium vulns cap at Medium risk
  } else {
    maxPossible = 29   // Low vulns cap at Low risk
  }

  // Raw score with diminishing returns
  const rawScore = criticalScore + highScore + mediumScore + lowScore

  // Normalize: Use sigmoid-like scaling to map to 0-maxPossible range
  // The formula gradually approaches maxPossible as rawScore increases
  // Score of ~50 raw = 50% of max, ~100 raw = 75% of max, ~200 raw = 90% of max
  const normalizedScore = maxPossible * (1 - Math.exp(-rawScore / 80))

  return Math.round(Math.min(normalizedScore, maxPossible))
}

// Get risk level label
function getRiskLevel(score: number): { label: string; color: string; bg: string } {
  if (score >= 80) return { label: 'Critical', color: 'text-red-700', bg: 'bg-red-500' }
  if (score >= 60) return { label: 'High', color: 'text-orange-700', bg: 'bg-orange-500' }
  if (score >= 40) return { label: 'Medium', color: 'text-yellow-700', bg: 'bg-yellow-500' }
  if (score >= 20) return { label: 'Low', color: 'text-blue-700', bg: 'bg-blue-500' }
  return { label: 'Minimal', color: 'text-green-700', bg: 'bg-green-500' }
}

// Remediation status configuration
const REMEDIATION_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-gray-100 text-gray-700' },
  { value: 'acknowledged', label: 'Acknowledged', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'remediated', label: 'Remediated', color: 'bg-green-100 text-green-700' },
  { value: 'accepted_risk', label: 'Risk Accepted', color: 'bg-purple-100 text-purple-700' },
  { value: 'false_positive', label: 'False Positive', color: 'bg-orange-100 text-orange-700' },
  { value: 'deferred', label: 'Deferred', color: 'bg-gray-200 text-gray-600' },
] as const

type RemediationStatusValue = typeof REMEDIATION_STATUSES[number]['value']

function getRemediationStatusColor(status: string | null | undefined): string {
  const found = REMEDIATION_STATUSES.find(s => s.value === status)
  return found?.color || 'bg-gray-100 text-gray-700'
}

function getRemediationStatusLabel(status: string | null | undefined): string {
  const found = REMEDIATION_STATUSES.find(s => s.value === status)
  return found?.label || 'New'
}

// Result Detail Modal Component - Professional Scanner Report Style
function ResultModal({
  result,
  vulnerabilities,
  onClose,
  onMarkFalsePositive,
  onValidatePoc,
  onUpdateRemediation,
  pocCredits,
  validatingVulnId,
  users,
}: {
  result: ScanResult
  vulnerabilities: Vulnerability[]
  onClose: () => void
  onMarkFalsePositive: (id: string, isFP: boolean, reason?: string) => void
  onValidatePoc: (vulnId: string) => Promise<void>
  onUpdateRemediation: (vulnId: string, data: {
    status?: RemediationStatusValue
    assigned_to?: string | null
    due_date?: number | null
    notes?: string | null
  }) => Promise<void>
  pocCredits: PocCredits | null
  validatingVulnId: string | null
  users: Array<{ id: string; username: string; first_name?: string; last_name?: string }>
}) {
  const { formatDate, formatDateTime } = useDatePreferences()
  const [expandedVuln, setExpandedVuln] = useState<string | null>(null)
  const [falsePositiveReason, setFalsePositiveReason] = useState('')
  const [viewMode, setViewMode] = useState<'severity' | 'host'>('severity')
  const [severityFilter, setSeverityFilter] = useState<string | null>(null)
  const [showFalsePositives, setShowFalsePositives] = useState(false)
  // Remediation state (#122)
  const [editingRemediation, setEditingRemediation] = useState<string | null>(null)
  const [remediationStatus, setRemediationStatus] = useState<RemediationStatusValue>('new')
  const [remediationAssignee, setRemediationAssignee] = useState<string>('')
  const [remediationDueDate, setRemediationDueDate] = useState<string>('')
  const [remediationNotes, setRemediationNotes] = useState<string>('')
  const [savingRemediation, setSavingRemediation] = useState(false)
  const toast = useToast()

  // AI Analysis state (Issue #261)
  const [aiAnalysis, setAiAnalysis] = useState<{
    executive_summary: string
    risk_assessment: string
    key_findings: string[]
    attack_vectors: string[]
    priority_remediation: Array<{ issue: string; action: string; priority: string }>
    security_posture: { strengths: string[]; weaknesses: string[] }
    compliance_impact: string
  } | null>(null)
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false)
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null)
  const [showAiAnalysis, setShowAiAnalysis] = useState(false)

  // Load AI analysis when button clicked
  const loadAiAnalysis = async () => {
    if (aiAnalysis) {
      setShowAiAnalysis(true)
      return
    }
    setAiAnalysisLoading(true)
    setAiAnalysisError(null)
    try {
      const response = await api.getScanAIAnalysis(result.id)
      if (response.success && response.analysis) {
        setAiAnalysis(response.analysis)
        setShowAiAnalysis(true)
      } else {
        setAiAnalysisError('Failed to generate AI analysis')
      }
    } catch (err) {
      console.error('AI Analysis error:', err)
      setAiAnalysisError(err instanceof Error ? err.message : 'AI analysis failed')
    } finally {
      setAiAnalysisLoading(false)
    }
  }

  // Scope section expand state (Issue #261)
  const [expandedScopeSection, setExpandedScopeSection] = useState<string | null>(null)

  // Parse scope data (Issue #261)
  const scopeData = {
    subdomains: (() => { try { return result.subdomains ? JSON.parse(result.subdomains) : [] } catch { return [] } })(),
    liveHosts: (() => { try { return result.live_hosts ? JSON.parse(result.live_hosts) : [] } catch { return [] } })(),
    discoveredUrls: (() => { try { return result.discovered_urls ? JSON.parse(result.discovered_urls) : [] } catch { return [] } })(),
    jsFiles: (() => { try { return result.js_files ? JSON.parse(result.js_files) : [] } catch { return [] } })(),
    apiEndpoints: (() => { try { return result.api_endpoints ? JSON.parse(result.api_endpoints) : [] } catch { return [] } })(),
    directories: (() => { try { return result.directories ? JSON.parse(result.directories) : [] } catch { return [] } })(),
  }
  const hasScopeData = scopeData.subdomains.length > 0 || scopeData.liveHosts.length > 0 ||
    scopeData.discoveredUrls.length > 0 || scopeData.jsFiles.length > 0 ||
    scopeData.apiEndpoints.length > 0 || scopeData.directories.length > 0

  const severityOrder = ['critical', 'high', 'medium', 'low', 'info']

  // Filter vulnerabilities
  const filteredVulns = vulnerabilities.filter(v => {
    if (!showFalsePositives && v.is_false_positive) return false
    if (severityFilter && v.severity !== severityFilter) return false
    return true
  })

  // Sort by severity
  const sortedVulns = [...filteredVulns].sort((a, b) => {
    return severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity)
  })

  // Group by host
  const vulnsByHost = sortedVulns.reduce((acc, vuln) => {
    const host = vuln.host || 'Unknown Host'
    if (!acc[host]) acc[host] = []
    acc[host].push(vuln)
    return acc
  }, {} as Record<string, Vulnerability[]>)

  // Calculate stats
  const riskScore = calculateRiskScore(result)
  const riskLevel = getRiskLevel(riskScore)
  const uniqueHosts = [...new Set(vulnerabilities.map(v => v.host))].length
  const falsePositiveCount = vulnerabilities.filter(v => v.is_false_positive).length
  const activeVulnCount = vulnerabilities.length - falsePositiveCount

  // Get unique CVEs
  const uniqueCVEs = [...new Set(vulnerabilities
    .map(v => v.vulnerability_id)
    .filter(id => id?.startsWith('CVE-'))
  )]

  const VulnerabilityCard = ({ vuln }: { vuln: Vulnerability }) => {
    const isExpanded = expandedVuln === vuln.id
    const displayName = formatVulnName(vuln)
    const category = getVulnCategory(vuln)

    const severityColors: Record<string, { border: string; bg: string; icon: string }> = {
      critical: { border: 'border-l-red-500', bg: 'bg-red-50', icon: 'text-red-600' },
      high: { border: 'border-l-orange-500', bg: 'bg-orange-50', icon: 'text-orange-600' },
      medium: { border: 'border-l-yellow-500', bg: 'bg-yellow-50', icon: 'text-yellow-600' },
      low: { border: 'border-l-blue-500', bg: 'bg-blue-50', icon: 'text-blue-600' },
      info: { border: 'border-l-gray-400', bg: 'bg-gray-50', icon: 'text-gray-500' },
    }
    const colors = severityColors[vuln.severity] || severityColors.info

    return (
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${
        vuln.is_false_positive ? 'opacity-50' : ''
      }`}>
        <div
          className={`border-l-4 ${colors.border} cursor-pointer hover:bg-gray-50 transition-colors`}
          onClick={() => setExpandedVuln(isExpanded ? null : vuln.id)}
        >
          <div className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.icon}`}>
                    {vuln.severity.toUpperCase()}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${category.color}`}>
                    {category.label}
                  </span>
                  {vuln.is_false_positive === 1 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                      False Positive
                    </span>
                  )}
                  {/* POC Exploitability Status Badge */}
                  {vuln.exploitability_status === 'exploitable' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-600 text-white">
                      <Target className="h-3 w-3" />
                      CONFIRMED EXPLOITABLE
                    </span>
                  )}
                  {vuln.exploitability_status === 'not_exploitable' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                      <CheckCircle className="h-3 w-3" />
                      Not Exploitable
                    </span>
                  )}
                  {vuln.exploitability_status === 'inconclusive' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                      <AlertCircle className="h-3 w-3" />
                      Inconclusive - Manual Review Needed
                    </span>
                  )}
                  {vuln.exploitability_status === 'validating' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Validating...
                    </span>
                  )}
                </div>
                <h4 className="mt-2 text-base font-semibold text-gray-900">
                  {displayName}
                </h4>
                <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
                  <span className="font-mono">{vuln.host}{vuln.port ? `:${vuln.port}` : ''}</span>
                  {vuln.service && (
                    <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">
                      {vuln.service}
                    </span>
                  )}
                </div>
                {vuln.description && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                    {vuln.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {vuln.cvss_score !== null && vuln.cvss_score > 0 && (
                  <div className="text-center">
                    <div className={`text-lg font-bold ${
                      vuln.cvss_score >= 9 ? 'text-red-600' :
                      vuln.cvss_score >= 7 ? 'text-orange-600' :
                      vuln.cvss_score >= 4 ? 'text-yellow-600' :
                      'text-gray-600'
                    }`}>
                      {vuln.cvss_score.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-400">CVSS</div>
                  </div>
                )}
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-4">
            {/* Vulnerability ID */}
            {vuln.vulnerability_id && (
              <div>
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Identifier
                </h5>
                <code className="text-sm bg-white px-2 py-1 rounded border border-gray-200">
                  {vuln.vulnerability_id}
                </code>
              </div>
            )}

            {/* Description */}
            {vuln.description && (
              <div>
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Description
                </h5>
                <p className="text-sm text-gray-700 leading-relaxed">{vuln.description}</p>
              </div>
            )}

            {/* Affected Software & Indicators */}
            {(vuln.affected_software || vuln.exploit_available || vuln.patch_available !== null) && (
              <div className="flex flex-wrap gap-4">
                {vuln.affected_software && (
                  <div className="flex-1 min-w-[200px]">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Affected Software
                    </h5>
                    <p className="text-sm text-gray-700">{vuln.affected_software}</p>
                  </div>
                )}
                <div className="flex gap-3">
                  {vuln.exploit_available === 1 && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                      <span className="text-red-600 text-lg">!!!</span>
                      <span className="text-xs font-medium text-red-700">Exploit Available</span>
                    </div>
                  )}
                  {vuln.patch_available === 1 && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                      <span className="text-green-600 text-lg">&#10003;</span>
                      <span className="text-xs font-medium text-green-700">Patch Available</span>
                    </div>
                  )}
                  {vuln.patch_available === 0 && vuln.exploit_available === 1 && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-amber-600 text-lg">!</span>
                      <span className="text-xs font-medium text-amber-700">No Patch</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Solution/Remediation */}
            {vuln.solution && (
              <div>
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Remediation
                </h5>
                <div className="text-sm text-gray-700 bg-green-50 border border-green-200 rounded p-3">
                  {vuln.solution}
                </div>
              </div>
            )}

            {/* References */}
            {vuln.reference_urls && (
              <div>
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  References
                </h5>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    try {
                      const urls = JSON.parse(vuln.reference_urls)
                      return urls.slice(0, 5).map((url: string, i: number) => {
                        // Extract domain for display
                        let displayText = url
                        try {
                          const urlObj = new URL(url)
                          displayText = urlObj.hostname.replace('www.', '')
                        } catch {}

                        return (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs text-primary-600 hover:bg-primary-50 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {displayText}
                          </a>
                        )
                      })
                    } catch {
                      return null
                    }
                  })()}
                </div>
              </div>
            )}

            {/* Technical Evidence / IRR Section (Issue #261 - v62) */}
            {vuln.proof_data && (() => {
              try {
                const proof = JSON.parse(vuln.proof_data)
                const hasEvidence = proof.request || proof.response || proof.curl_command || proof.matched_at
                if (!hasEvidence) return null

                return (
                  <div className="pt-3 border-t border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <FileCode className="h-4 w-4" />
                      Technical Evidence
                    </h5>
                    <div className="space-y-3">
                      {/* Matched At */}
                      {proof.matched_at && (
                        <div>
                          <span className="text-xs text-gray-500 font-medium">Matched At:</span>
                          <code className="ml-2 text-xs bg-white px-2 py-1 rounded border border-gray-200 break-all">
                            {proof.matched_at}
                          </code>
                        </div>
                      )}

                      {/* Template ID */}
                      {proof.template_id && (
                        <div>
                          <span className="text-xs text-gray-500 font-medium">Template:</span>
                          <code className="ml-2 text-xs bg-white px-2 py-1 rounded border border-gray-200">
                            {proof.template_id}
                          </code>
                        </div>
                      )}

                      {/* cURL Command - Copy button */}
                      {proof.curl_command && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500 font-medium">cURL Command (Reproduce):</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigator.clipboard.writeText(proof.curl_command)
                              }}
                              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800"
                            >
                              <Copy className="h-3 w-3" />
                              Copy
                            </button>
                          </div>
                          <pre className="p-2 bg-gray-900 text-green-400 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-24">
                            {proof.curl_command}
                          </pre>
                        </div>
                      )}

                      {/* HTTP Request (Collapsible) */}
                      {proof.request && (
                        <details className="group">
                          <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1">
                            <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                            HTTP Request
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-900 text-blue-300 rounded text-xs font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                            {proof.request}
                          </pre>
                        </details>
                      )}

                      {/* HTTP Response (Collapsible) */}
                      {proof.response && (
                        <details className="group">
                          <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1">
                            <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                            HTTP Response
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-900 text-amber-300 rounded text-xs font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                            {proof.response}
                          </pre>
                        </details>
                      )}

                      {/* Extracted Results */}
                      {proof.extracted_results && proof.extracted_results.length > 0 && (
                        <div>
                          <span className="text-xs text-gray-500 font-medium">Extracted Data:</span>
                          <div className="mt-1 flex flex-wrap gap-2">
                            {proof.extracted_results.map((result: string, i: number) => (
                              <code key={i} className="text-xs bg-yellow-50 border border-yellow-200 text-yellow-800 px-2 py-1 rounded">
                                {result}
                              </code>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              } catch {
                return null
              }
            })()}

            {/* POC Validation Section */}
            {!vuln.is_false_positive && (vuln.severity === 'critical' || vuln.severity === 'high' || vuln.severity === 'medium') && (
              <div className="pt-3 border-t border-gray-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      POC Validation
                    </h5>
                    <p className="text-xs text-gray-500">
                      Test if this vulnerability is actually exploitable
                    </p>
                  </div>
                  {!vuln.exploitability_status || vuln.exploitability_status === 'unknown' || vuln.exploitability_status === 'validating' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onValidatePoc(vuln.id)
                      }}
                      disabled={validatingVulnId === vuln.id || vuln.exploitability_status === 'validating' || (pocCredits?.balance ?? 0) < 1}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                      {validatingVulnId === vuln.id || vuln.exploitability_status === 'validating' ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Validating...
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4" />
                          Validate Exploitability
                          <span className="text-xs opacity-75">($29.99)</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      vuln.exploitability_status === 'exploitable' ? 'bg-red-100 text-red-800' :
                      vuln.exploitability_status === 'not_exploitable' ? 'bg-green-100 text-green-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {vuln.exploitability_status === 'exploitable' && 'Confirmed Exploitable'}
                      {vuln.exploitability_status === 'not_exploitable' && 'Not Exploitable'}
                      {vuln.exploitability_status === 'inconclusive' && 'Inconclusive - Manual Review Needed'}
                    </div>
                  )}
                </div>

                {/* POC Evidence Display - Show when validation completed */}
                {vuln.poc_validation_id && vuln.poc_evidence_summary && (
                  <div className={`rounded-lg border p-4 space-y-3 ${
                    vuln.poc_result === 'exploitable' ? 'bg-red-50 border-red-200' :
                    vuln.poc_result === 'not_exploitable' ? 'bg-green-50 border-green-200' :
                    'bg-yellow-50 border-yellow-200'
                  }`}>
                    {/* Evidence Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Target className={`h-5 w-5 ${
                          vuln.poc_result === 'exploitable' ? 'text-red-600' :
                          vuln.poc_result === 'not_exploitable' ? 'text-green-600' :
                          'text-yellow-600'
                        }`} />
                        <span className="font-semibold text-gray-900">Validation Evidence</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {vuln.poc_validation_method && (
                          <span className="px-2 py-1 bg-white rounded border border-gray-200">
                            Method: {vuln.poc_validation_method}
                          </span>
                        )}
                        {vuln.poc_confidence_score !== null && vuln.poc_confidence_score !== undefined && (
                          <span className={`px-2 py-1 rounded border ${
                            vuln.poc_confidence_score >= 0.8 ? 'bg-red-100 border-red-300 text-red-700' :
                            vuln.poc_confidence_score >= 0.5 ? 'bg-yellow-100 border-yellow-300 text-yellow-700' :
                            'bg-gray-100 border-gray-300 text-gray-700'
                          }`}>
                            Confidence: {Math.round(vuln.poc_confidence_score * 100)}%
                          </span>
                        )}
                        {vuln.poc_duration && (
                          <span>Duration: {vuln.poc_duration}s</span>
                        )}
                      </div>
                    </div>

                    {/* Evidence Summary */}
                    <div className="text-sm text-gray-800 leading-relaxed">
                      {vuln.poc_evidence_summary}
                    </div>

                    {/* Raw Output (Collapsible) */}
                    {vuln.poc_output && (
                      <details className="group">
                        <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1">
                          <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                          View Raw Tool Output
                        </summary>
                        <pre className="mt-2 p-3 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
                          {vuln.poc_output}
                        </pre>
                      </details>
                    )}
                  </div>
                )}

                {pocCredits !== null && pocCredits.balance < 1 && !vuln.exploitability_status && (
                  <p className="mt-2 text-xs text-amber-600">
                    No credits remaining. Purchase credits to validate vulnerabilities.
                  </p>
                )}
              </div>
            )}

            {/* Remediation Tracking Section (#122) */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-gray-500" />
                  <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Remediation Status
                  </h5>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded ${getRemediationStatusColor(vuln.remediation_status)}`}>
                  {getRemediationStatusLabel(vuln.remediation_status)}
                </span>
              </div>

              {editingRemediation === vuln.id ? (
                <div className="space-y-3 bg-white border border-gray-200 rounded-lg p-3" onClick={(e) => e.stopPropagation()}>
                  {/* Status Dropdown */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={remediationStatus}
                      onChange={(e) => setRemediationStatus(e.target.value as RemediationStatusValue)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {REMEDIATION_STATUSES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Assignee */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Assigned To</label>
                    <select
                      value={remediationAssignee}
                      onChange={(e) => setRemediationAssignee(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Unassigned</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Due Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                    <input
                      type="date"
                      value={remediationDueDate}
                      onChange={(e) => setRemediationDueDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={remediationNotes}
                      onChange={(e) => setRemediationNotes(e.target.value)}
                      rows={2}
                      placeholder="Add remediation notes..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setEditingRemediation(null)
                        setRemediationStatus('new')
                        setRemediationAssignee('')
                        setRemediationDueDate('')
                        setRemediationNotes('')
                      }}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        setSavingRemediation(true)
                        try {
                          await onUpdateRemediation(vuln.id, {
                            status: remediationStatus,
                            assigned_to: remediationAssignee || null,
                            due_date: remediationDueDate ? new Date(remediationDueDate).getTime() : null,
                            notes: remediationNotes || null,
                          })
                          toast.success('Remediation status updated')
                          setEditingRemediation(null)
                        } catch (err) {
                          toast.error('Failed to update remediation status')
                        } finally {
                          setSavingRemediation(false)
                        }
                      }}
                      disabled={savingRemediation}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                    >
                      {savingRemediation ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Current Status Display */}
                  <div className="flex flex-wrap gap-3 text-sm">
                    {vuln.assigned_to_username && (
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <User className="h-3.5 w-3.5" />
                        <span>Assigned to: {vuln.assigned_to_username}</span>
                      </div>
                    )}
                    {vuln.remediation_due_date && (
                      <div className={`flex items-center gap-1.5 ${
                        vuln.remediation_due_date < Date.now() ? 'text-red-600 font-medium' : 'text-gray-600'
                      }`}>
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Due: {formatDate(vuln.remediation_due_date)}</span>
                        {vuln.remediation_due_date < Date.now() && (
                          <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">Overdue</span>
                        )}
                      </div>
                    )}
                    {vuln.remediated_at && (
                      <div className="flex items-center gap-1.5 text-green-600">
                        <CheckCircle className="h-3.5 w-3.5" />
                        <span>Remediated: {formatDate(vuln.remediated_at)}</span>
                      </div>
                    )}
                  </div>

                  {/* Notes Display */}
                  {vuln.remediation_notes && (
                    <div className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                      <span className="font-medium text-gray-700">Notes: </span>
                      {vuln.remediation_notes}
                    </div>
                  )}

                  {/* Edit Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditingRemediation(vuln.id)
                      setRemediationStatus((vuln.remediation_status as RemediationStatusValue) || 'new')
                      setRemediationAssignee(vuln.assigned_to || '')
                      setRemediationDueDate(vuln.remediation_due_date ? new Date(vuln.remediation_due_date).toISOString().split('T')[0] : '')
                      setRemediationNotes(vuln.remediation_notes || '')
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                  >
                    <FileEdit className="h-4 w-4" />
                    Update Remediation Status
                  </button>
                </div>
              )}
            </div>

            {/* False Positive Actions */}
            <div className="pt-3 border-t border-gray-200">
              {!vuln.is_false_positive ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Reason for marking as false positive..."
                    value={falsePositiveReason}
                    onChange={(e) => setFalsePositiveReason(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onMarkFalsePositive(vuln.id, true, falsePositiveReason)
                      setFalsePositiveReason('')
                    }}
                    className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors"
                  >
                    Mark False Positive
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-orange-50 rounded-lg p-3">
                  <div>
                    <p className="text-sm font-medium text-orange-800">Marked as False Positive</p>
                    {vuln.false_positive_reason && (
                      <p className="text-xs text-orange-600 mt-0.5">Reason: {vuln.false_positive_reason}</p>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onMarkFalsePositive(vuln.id, false)
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Restore
                  </button>
                </div>
              )}
            </div>

            {/* Linked Controls Section (#240) - Component not yet implemented */}
            {/* <VulnerabilityControlsSection vulnId={vuln.id} /> */}

            {/* Jira Ticket Section (#125) */}
            <JiraTicketSection vulnId={vuln.id} vuln={vuln} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Security Scan Report</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {result.config_name} • {formatDateTime(result.started_at, {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* AI Analysis Button (#261) */}
            <button
              onClick={loadAiAnalysis}
              disabled={aiAnalysisLoading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {aiAnalysisLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {aiAnalysisLoading ? 'Analyzing...' : 'AI Analysis'}
            </button>
            {/* Export Dropdown (#185) */}
            <ExportDropdown resultId={result.id} />
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* AI Analysis Panel (#261) */}
        {showAiAnalysis && aiAnalysis && (
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">AI Security Analysis</h3>
                <span className="text-xs text-gray-500">Powered by Cloudflare Workers AI</span>
              </div>
              <button
                onClick={() => setShowAiAnalysis(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-12 gap-4">
              {/* Executive Summary */}
              <div className="col-span-8">
                <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Executive Summary</h4>
                  <p className="text-sm text-gray-700">{aiAnalysis.executive_summary}</p>

                  {/* Key Findings */}
                  {aiAnalysis.key_findings && aiAnalysis.key_findings.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Key Findings</h4>
                      <ul className="space-y-1">
                        {aiAnalysis.key_findings.map((finding, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                            {finding}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Risk Assessment */}
              <div className="col-span-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">AI Risk Assessment</h4>
                  <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${
                    aiAnalysis.risk_assessment === 'Critical' ? 'bg-red-100 text-red-700' :
                    aiAnalysis.risk_assessment === 'High' ? 'bg-orange-100 text-orange-700' :
                    aiAnalysis.risk_assessment === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                    aiAnalysis.risk_assessment === 'Low' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {aiAnalysis.risk_assessment}
                  </div>

                  {/* Compliance Impact */}
                  {aiAnalysis.compliance_impact && (
                    <div className="mt-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Compliance Impact</h4>
                      <p className="text-xs text-gray-600">{aiAnalysis.compliance_impact}</p>
                    </div>
                  )}
                </div>

                {/* Security Posture */}
                {aiAnalysis.security_posture && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-3">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Security Posture</h4>
                    {aiAnalysis.security_posture.strengths && aiAnalysis.security_posture.strengths.length > 0 && (
                      <div className="mb-2">
                        <span className="text-xs font-medium text-green-600">Strengths:</span>
                        <ul className="text-xs text-gray-600 ml-3 mt-1">
                          {aiAnalysis.security_posture.strengths.slice(0, 2).map((s, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {aiAnalysis.security_posture.weaknesses && aiAnalysis.security_posture.weaknesses.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-red-600">Weaknesses:</span>
                        <ul className="text-xs text-gray-600 ml-3 mt-1">
                          {aiAnalysis.security_posture.weaknesses.slice(0, 2).map((w, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <XCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                              {w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Priority Remediation */}
              {aiAnalysis.priority_remediation && aiAnalysis.priority_remediation.length > 0 && (
                <div className="col-span-12">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Priority Remediation Actions</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {aiAnalysis.priority_remediation.slice(0, 6).map((item, idx) => (
                        <div key={idx} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              item.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                              item.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                              item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {item.priority}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-800">{item.issue}</p>
                          <p className="text-xs text-gray-600 mt-1">{item.action}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Analysis Error */}
        {aiAnalysisError && (
          <div className="px-6 py-3 border-b border-gray-200 bg-red-50">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">{aiAnalysisError}</span>
              <button
                onClick={loadAiAnalysis}
                className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Executive Summary */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-12 gap-6">
            {/* Risk Score */}
            <div className="col-span-3">
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Risk Score
                </div>
                <div className="relative w-24 h-24 mx-auto">
                  <svg className="w-24 h-24 transform -rotate-90">
                    <circle
                      cx="48" cy="48" r="40"
                      stroke="#e5e7eb" strokeWidth="8" fill="none"
                    />
                    <circle
                      cx="48" cy="48" r="40"
                      stroke={riskScore >= 80 ? '#ef4444' : riskScore >= 60 ? '#f97316' : riskScore >= 40 ? '#eab308' : riskScore >= 20 ? '#3b82f6' : '#22c55e'}
                      strokeWidth="8" fill="none"
                      strokeDasharray={`${(riskScore / 100) * 251.2} 251.2`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-2xl font-bold ${riskLevel.color}`}>{riskScore}</span>
                  </div>
                </div>
                <div className={`mt-2 text-sm font-semibold ${riskLevel.color}`}>
                  {riskLevel.label} Risk
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="col-span-5">
              <div className="grid grid-cols-2 gap-3 h-full">
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="text-2xl font-bold text-gray-900">{activeVulnCount}</div>
                  <div className="text-xs text-gray-500">Active Vulnerabilities</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="text-2xl font-bold text-gray-900">{uniqueHosts}</div>
                  <div className="text-xs text-gray-500">Hosts Affected</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="text-2xl font-bold text-gray-900">{uniqueCVEs.length}</div>
                  <div className="text-xs text-gray-500">Unique CVEs</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="text-2xl font-bold text-gray-900">{result.duration ? `${Math.round(result.duration)}s` : '-'}</div>
                  <div className="text-xs text-gray-500">Scan Duration</div>
                </div>
              </div>
            </div>

            {/* Severity Breakdown */}
            <div className="col-span-4">
              <div className="bg-white rounded-lg border border-gray-200 p-3 h-full">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Severity Distribution
                </div>
                <div className="space-y-2">
                  {[
                    { key: 'critical', count: result.critical_count, color: 'bg-red-500' },
                    { key: 'high', count: result.high_count, color: 'bg-orange-500' },
                    { key: 'medium', count: result.medium_count, color: 'bg-yellow-500' },
                    { key: 'low', count: result.low_count, color: 'bg-blue-500' },
                    { key: 'info', count: result.info_count, color: 'bg-gray-400' },
                  ].map(({ key, count, color }) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-16 capitalize">{key}</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${color} rounded-full transition-all`}
                          style={{ width: `${result.vulnerabilities_found > 0 ? (count / result.vulnerabilities_found) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-700 w-8 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* MITRE ATT&CK Kill Chain Analysis */}
          {(() => {
            const mitreTactics = aggregateMitreTactics(vulnerabilities)
            if (mitreTactics.length === 0) return null

            return (
              <div className="mt-5 bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="h-4 w-4 text-slate-600" />
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    MITRE ATT&CK Kill Chain
                  </div>
                  <a
                    href="https://attack.mitre.org/matrices/enterprise/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 ml-auto"
                  >
                    View Framework
                  </a>
                </div>
                <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
                  {mitreTactics.map(({ tactic, count, severity }, idx) => {
                    const maxSeverity = severity.critical > 0 ? 'critical' :
                      severity.high > 0 ? 'high' :
                      severity.medium > 0 ? 'medium' : 'low'
                    const severityBorder = {
                      critical: 'border-red-500',
                      high: 'border-orange-500',
                      medium: 'border-yellow-500',
                      low: 'border-blue-500'
                    }[maxSeverity]

                    return (
                      <div key={tactic.id} className="flex items-center">
                        <div
                          className={`relative flex flex-col items-center justify-center px-3 py-2 rounded-lg border-2 ${severityBorder} bg-gray-50 min-w-[100px] hover:bg-gray-100 transition-colors cursor-default group`}
                          title={`${tactic.name} (${tactic.id})\n${tactic.description}\n\nVulnerabilities: ${count}\nCritical: ${severity.critical}, High: ${severity.high}, Medium: ${severity.medium}, Low: ${severity.low}`}
                        >
                          <div className="text-[10px] font-mono text-gray-400">{tactic.id}</div>
                          <div className="text-xs font-semibold text-gray-800 text-center leading-tight">
                            {tactic.name.replace(' ', '\n')}
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            {severity.critical > 0 && <span className="w-2 h-2 rounded-full bg-red-500" title={`${severity.critical} critical`} />}
                            {severity.high > 0 && <span className="w-2 h-2 rounded-full bg-orange-500" title={`${severity.high} high`} />}
                            {severity.medium > 0 && <span className="w-2 h-2 rounded-full bg-yellow-500" title={`${severity.medium} medium`} />}
                            {severity.low > 0 && <span className="w-2 h-2 rounded-full bg-blue-500" title={`${severity.low} low`} />}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5">{count} vuln{count !== 1 ? 's' : ''}</div>
                        </div>
                        {idx < mitreTactics.length - 1 && (
                          <ChevronRight className="h-4 w-4 text-gray-300 mx-0.5 flex-shrink-0" />
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span> Critical
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span> High
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span> Medium
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span> Low
                  </span>
                </div>
              </div>
            )
          })()}
        </div>

        {/* Reconnaissance & Discovery Section (Issue #261) */}
        {hasScopeData && (
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="h-5 w-5 text-slate-600" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Reconnaissance & Discovery</h3>
              <span className="text-xs text-gray-500">
                {scopeData.subdomains.length + scopeData.liveHosts.length + scopeData.discoveredUrls.length + scopeData.jsFiles.length + scopeData.directories.length} items discovered
              </span>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {/* Subdomains */}
              {scopeData.subdomains.length > 0 && (
                <button
                  onClick={() => setExpandedScopeSection(expandedScopeSection === 'subdomains' ? null : 'subdomains')}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                    expandedScopeSection === 'subdomains'
                      ? 'bg-cyan-50 border-cyan-300 ring-2 ring-cyan-200'
                      : 'bg-white border-gray-200 hover:border-cyan-300 hover:bg-cyan-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-cyan-600" />
                    <span className="text-xs font-medium text-gray-700">Subdomains</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-bold text-green-600">{scopeData.liveHosts.length}</span>
                    <span className="text-xs text-gray-400">/</span>
                    <span className="text-xs font-bold text-cyan-600">{scopeData.subdomains.length}</span>
                  </div>
                </button>
              )}
              {/* Live Hosts */}
              {scopeData.liveHosts.length > 0 && (
                <button
                  onClick={() => setExpandedScopeSection(expandedScopeSection === 'liveHosts' ? null : 'liveHosts')}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                    expandedScopeSection === 'liveHosts'
                      ? 'bg-green-50 border-green-300 ring-2 ring-green-200'
                      : 'bg-white border-gray-200 hover:border-green-300 hover:bg-green-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Server className="h-4 w-4 text-green-600" />
                    <span className="text-xs font-medium text-gray-700">Live Hosts</span>
                  </div>
                  <span className="text-xs font-bold text-green-600">{scopeData.liveHosts.length}</span>
                </button>
              )}
              {/* URLs */}
              {scopeData.discoveredUrls.length > 0 && (
                <button
                  onClick={() => setExpandedScopeSection(expandedScopeSection === 'urls' ? null : 'urls')}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                    expandedScopeSection === 'urls'
                      ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200'
                      : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Link className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-gray-700">URLs</span>
                  </div>
                  <span className="text-xs font-bold text-blue-600">{scopeData.discoveredUrls.length}</span>
                </button>
              )}
              {/* JS Files */}
              {scopeData.jsFiles.length > 0 && (
                <button
                  onClick={() => setExpandedScopeSection(expandedScopeSection === 'jsFiles' ? null : 'jsFiles')}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                    expandedScopeSection === 'jsFiles'
                      ? 'bg-yellow-50 border-yellow-300 ring-2 ring-yellow-200'
                      : 'bg-white border-gray-200 hover:border-yellow-300 hover:bg-yellow-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FileCode className="h-4 w-4 text-yellow-600" />
                    <span className="text-xs font-medium text-gray-700">JS Files</span>
                  </div>
                  <span className="text-xs font-bold text-yellow-600">{scopeData.jsFiles.length}</span>
                </button>
              )}
              {/* API Endpoints */}
              {scopeData.apiEndpoints.length > 0 && (
                <button
                  onClick={() => setExpandedScopeSection(expandedScopeSection === 'api' ? null : 'api')}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                    expandedScopeSection === 'api'
                      ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-200'
                      : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Webhook className="h-4 w-4 text-purple-600" />
                    <span className="text-xs font-medium text-gray-700">API Endpoints</span>
                  </div>
                  <span className="text-xs font-bold text-purple-600">{scopeData.apiEndpoints.length}</span>
                </button>
              )}
              {/* Directories */}
              {scopeData.directories.length > 0 && (
                <button
                  onClick={() => setExpandedScopeSection(expandedScopeSection === 'directories' ? null : 'directories')}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                    expandedScopeSection === 'directories'
                      ? 'bg-orange-50 border-orange-300 ring-2 ring-orange-200'
                      : 'bg-white border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-orange-600" />
                    <span className="text-xs font-medium text-gray-700">Directories</span>
                  </div>
                  <span className="text-xs font-bold text-orange-600">{scopeData.directories.length}</span>
                </button>
              )}
            </div>

            {/* Expanded Section Content */}
            {expandedScopeSection && (
              <div className="mt-3 bg-white rounded-lg border border-gray-200 max-h-48 overflow-y-auto">
                <div className="p-3 space-y-1">
                  {expandedScopeSection === 'subdomains' && (
                    <>
                      <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded-lg mb-2">
                        <span className="text-xs font-medium text-gray-600">
                          {scopeData.liveHosts.length} of {scopeData.subdomains.length} resolved (live)
                        </span>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            <span className="text-green-600">Live</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <XCircle className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-500">No DNS</span>
                          </span>
                        </div>
                      </div>
                      {scopeData.subdomains.map((item: string, i: number) => {
                        const isLive = scopeData.liveHosts.includes(item)
                        return (
                          <div key={i} className={`flex items-center gap-2 py-1.5 px-2 rounded text-sm font-mono ${
                            isLive ? 'bg-green-50 text-green-800' : 'hover:bg-gray-50 text-gray-500'
                          }`}>
                            {isLive ? (
                              <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            )}
                            <span className="truncate">{item}</span>
                            {isLive && (
                              <span className="ml-auto text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">LIVE</span>
                            )}
                          </div>
                        )
                      })}
                    </>
                  )}
                  {expandedScopeSection === 'liveHosts' && scopeData.liveHosts.map((item: string, i: number) => (
                    <div key={i} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded text-sm font-mono text-gray-700">
                      <Server className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))}
                  {expandedScopeSection === 'urls' && scopeData.discoveredUrls.slice(0, 100).map((item: string, i: number) => (
                    <div key={i} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded text-sm font-mono text-gray-700">
                      <Link className="h-3 w-3 text-blue-500 flex-shrink-0" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))}
                  {expandedScopeSection === 'jsFiles' && scopeData.jsFiles.map((item: string, i: number) => (
                    <div key={i} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded text-sm font-mono text-gray-700">
                      <FileCode className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))}
                  {expandedScopeSection === 'api' && scopeData.apiEndpoints.map((item: string, i: number) => (
                    <div key={i} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded text-sm font-mono text-gray-700">
                      <Webhook className="h-3 w-3 text-purple-500 flex-shrink-0" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))}
                  {expandedScopeSection === 'directories' && scopeData.directories.slice(0, 100).map((item: string, i: number) => (
                    <div key={i} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded text-sm font-mono text-gray-700">
                      <FolderOpen className="h-3 w-3 text-orange-500 flex-shrink-0" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))}
                </div>
                {((expandedScopeSection === 'urls' && scopeData.discoveredUrls.length > 100) ||
                  (expandedScopeSection === 'directories' && scopeData.directories.length > 100)) && (
                  <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                    Showing first 100 of {expandedScopeSection === 'urls' ? scopeData.discoveredUrls.length : scopeData.directories.length} items
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filters & View Toggle */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Filter:</span>
            <button
              onClick={() => setSeverityFilter(null)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                severityFilter === null ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({filteredVulns.length})
            </button>
            {result.critical_count > 0 && (
              <button
                onClick={() => setSeverityFilter(severityFilter === 'critical' ? null : 'critical')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  severityFilter === 'critical' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                Critical ({result.critical_count})
              </button>
            )}
            {result.high_count > 0 && (
              <button
                onClick={() => setSeverityFilter(severityFilter === 'high' ? null : 'high')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  severityFilter === 'high' ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                High ({result.high_count})
              </button>
            )}
            {result.medium_count > 0 && (
              <button
                onClick={() => setSeverityFilter(severityFilter === 'medium' ? null : 'medium')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  severityFilter === 'medium' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                }`}
              >
                Medium ({result.medium_count})
              </button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showFalsePositives}
                onChange={(e) => setShowFalsePositives(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Show false positives ({falsePositiveCount})
            </label>
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('severity')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'severity' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                By Severity
              </button>
              <button
                onClick={() => setViewMode('host')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'host' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                By Host
              </button>
            </div>
          </div>
        </div>

        {/* Vulnerabilities List */}
        <div className="flex-1 overflow-auto p-6 bg-gray-50">
          {sortedVulns.length === 0 ? (
            <div className="space-y-6">
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 mx-auto text-green-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900">No Vulnerabilities Found</h3>
                <p className="text-gray-500 mt-1">
                  {severityFilter
                    ? `No ${severityFilter} severity vulnerabilities detected`
                    : 'Your scan completed without detecting any vulnerabilities'}
                </p>
              </div>

              {/* Discovered Hosts Section - Show even when no vulnerabilities */}
              {(() => {
                let discoveredHosts: DiscoveredHost[] = []
                try {
                  if (result.discovered_hosts) {
                    discoveredHosts = JSON.parse(result.discovered_hosts)
                  }
                } catch (e) {
                  console.error('Failed to parse discovered_hosts:', e)
                }

                if (discoveredHosts.length === 0) return null

                const totalPorts = discoveredHosts.reduce((sum, h) => sum + (h.ports?.length || 0), 0)

                return (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
                      <div className="flex items-center gap-3">
                        <Network className="h-5 w-5 text-blue-500" />
                        <h3 className="text-base font-semibold text-gray-900">Discovered Network Assets</h3>
                        <span className="text-sm text-gray-500">
                          {discoveredHosts.length} {discoveredHosts.length === 1 ? 'host' : 'hosts'} with {totalPorts} open {totalPorts === 1 ? 'port' : 'ports'}
                        </span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {discoveredHosts.map((host, hostIdx) => (
                        <div key={hostIdx} className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <Server className="h-4 w-4 text-gray-400" />
                            <span className="font-mono text-sm font-medium text-gray-900">{host.ip}</span>
                            {host.hostname && host.hostname !== host.ip && (
                              <>
                                <Globe className="h-4 w-4 text-gray-300" />
                                <span className="text-sm text-gray-600">{host.hostname}</span>
                              </>
                            )}
                            {host.os && (
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700" title={host.osAccuracy ? `${host.osAccuracy}% accuracy` : undefined}>
                                {host.os}
                              </span>
                            )}
                            <span className={`ml-auto px-2 py-0.5 text-xs font-medium rounded-full ${
                              host.state === 'up' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {host.state}
                            </span>
                          </div>
                          {(host.ports?.length || 0) > 0 ? (
                            <div className="ml-7 space-y-2">
                              {(host.ports || []).map((port, portIdx) => (
                                <div
                                  key={portIdx}
                                  className="flex items-center gap-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg"
                                >
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    port.state === 'open' ? 'bg-green-500' : 'bg-yellow-500'
                                  }`} title={port.state} />
                                  <span className="font-mono text-sm font-medium text-gray-900 w-16">
                                    {port.port}/{port.protocol}
                                  </span>
                                  <span className="text-sm font-medium text-gray-700 w-20">
                                    {port.service || 'unknown'}
                                  </span>
                                  {port.product && (
                                    <div className="flex-1">
                                      <span className="text-sm font-medium text-blue-600">
                                        {port.product}
                                      </span>
                                      {port.version && (
                                        <span className="text-sm text-gray-500 ml-2">
                                          v{port.version}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="ml-7 text-sm text-gray-400">No open ports detected</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </div>
          ) : viewMode === 'severity' ? (
            <div className="space-y-3">
              {sortedVulns.map((vuln) => (
                <VulnerabilityCard key={vuln.id} vuln={vuln} />
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(vulnsByHost).map(([host, hostVulns]) => (
                <div key={host}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg">
                      <Shield className="h-4 w-4 text-gray-400" />
                      <span className="font-mono text-sm font-medium text-gray-900">{host}</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {hostVulns.length} {hostVulns.length === 1 ? 'vulnerability' : 'vulnerabilities'}
                    </span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>
                  <div className="space-y-3 ml-4">
                    {hostVulns.map((vuln) => (
                      <VulnerabilityCard key={vuln.id} vuln={vuln} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Helper function to generate PDF scan report using jsPDF
async function generateScanReportPDF(data: {
  scan: {
    id: string
    config_name: string
    scan_type: string
    status: string
    started_at: string | null
    completed_at: string | null
    hosts_scanned: number
  }
  summary: {
    total_vulnerabilities: number
    critical: number
    high: number
    medium: number
    low: number
    info: number
  }
  discovered_hosts: Array<{
    ip: string
    hostname: string
    state: string
    ports: Array<{ port: number; protocol: string; state: string; service?: string }>
  }>
  vulnerabilities: Array<{
    host: string
    port: number
    vulnerability_name: string
    severity: string
    description: string
    solution?: string
  }>
}): Promise<Blob> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 15
  const contentWidth = pageWidth - margin * 2
  let yPos = margin

  const checkPageBreak = (needed: number) => {
    if (yPos + needed > pageHeight - margin) {
      doc.addPage()
      yPos = margin
      return true
    }
    return false
  }

  // Severity colors
  const severityColors: Record<string, [number, number, number]> = {
    critical: [220, 38, 38],
    high: [234, 88, 12],
    medium: [202, 138, 4],
    low: [37, 99, 235],
    info: [107, 114, 128],
  }

  // Title
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(10, 38, 71)
  doc.text('Vulnerability Scan Report', margin, yPos)
  yPos += 10

  // Subtitle
  doc.setFontSize(12)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 100, 100)
  doc.text(data.scan.config_name, margin, yPos)
  yPos += 8

  // Header line
  doc.setDrawColor(10, 38, 71)
  doc.setLineWidth(0.5)
  doc.line(margin, yPos, pageWidth - margin, yPos)
  yPos += 8

  // Metadata
  doc.setFontSize(10)
  doc.setTextColor(80, 80, 80)
  const scanDate = data.scan.started_at ? new Date(data.scan.started_at).toLocaleString() : 'N/A'
  doc.text(`Scan Date: ${scanDate}  |  Status: ${data.scan.status}  |  Hosts: ${data.scan.hosts_scanned}`, margin, yPos)
  yPos += 12

  // Summary Section
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(10, 38, 71)
  doc.text('Executive Summary', margin, yPos)
  yPos += 8

  // Summary boxes
  const boxWidth = (contentWidth - 20) / 5
  const boxHeight = 20
  const summaryData = [
    { label: 'Critical', count: data.summary.critical, color: severityColors.critical },
    { label: 'High', count: data.summary.high, color: severityColors.high },
    { label: 'Medium', count: data.summary.medium, color: severityColors.medium },
    { label: 'Low', count: data.summary.low, color: severityColors.low },
    { label: 'Info', count: data.summary.info, color: severityColors.info },
  ]

  summaryData.forEach((item, idx) => {
    const xPos = margin + idx * (boxWidth + 4)
    doc.setFillColor(item.color[0], item.color[1], item.color[2])
    doc.roundedRect(xPos, yPos, boxWidth, boxHeight, 2, 2, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text(String(item.count), xPos + boxWidth / 2, yPos + 10, { align: 'center' })
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.text(item.label, xPos + boxWidth / 2, yPos + 16, { align: 'center' })
  })
  yPos += boxHeight + 12

  // Discovered Hosts Section
  if (data.discovered_hosts && data.discovered_hosts.length > 0) {
    checkPageBreak(20)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(10, 38, 71)
    doc.text('Discovered Hosts', margin, yPos)
    yPos += 8

    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(50, 50, 50)

    for (const host of data.discovered_hosts.slice(0, 10)) {
      checkPageBreak(12)
      const ports = host.ports?.map(p => `${p.port}/${p.protocol}${p.service ? ` (${p.service})` : ''}`).join(', ') || 'No ports'
      doc.setFont('helvetica', 'bold')
      doc.text(`${host.hostname || host.ip}`, margin, yPos)
      doc.setFont('helvetica', 'normal')
      doc.text(` - ${host.state}`, margin + doc.getTextWidth(`${host.hostname || host.ip}`), yPos)
      yPos += 4
      const portLines = doc.splitTextToSize(`Ports: ${ports}`, contentWidth - 5)
      doc.text(portLines, margin + 3, yPos)
      yPos += portLines.length * 4 + 3
    }
    if (data.discovered_hosts.length > 10) {
      doc.setTextColor(100, 100, 100)
      doc.text(`... and ${data.discovered_hosts.length - 10} more hosts`, margin, yPos)
      yPos += 6
    }
    yPos += 6
  }

  // Vulnerabilities Section
  if (data.vulnerabilities && data.vulnerabilities.length > 0) {
    checkPageBreak(20)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(10, 38, 71)
    doc.text('Vulnerabilities', margin, yPos)
    yPos += 8

    // Group by severity
    const bySeverity: Record<string, typeof data.vulnerabilities> = {}
    for (const vuln of data.vulnerabilities) {
      const sev = vuln.severity || 'info'
      if (!bySeverity[sev]) bySeverity[sev] = []
      bySeverity[sev].push(vuln)
    }

    for (const severity of ['critical', 'high', 'medium', 'low', 'info']) {
      const vulns = bySeverity[severity]
      if (!vulns || vulns.length === 0) continue

      checkPageBreak(15)
      const color = severityColors[severity] || [100, 100, 100]
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(color[0], color[1], color[2])
      doc.text(`${severity.toUpperCase()} (${vulns.length})`, margin, yPos)
      yPos += 6

      doc.setFontSize(9)
      doc.setTextColor(50, 50, 50)

      for (const vuln of vulns.slice(0, 20)) {
        checkPageBreak(12)
        doc.setFont('helvetica', 'bold')
        const nameLines = doc.splitTextToSize(vuln.vulnerability_name, contentWidth - 10)
        doc.text(nameLines, margin + 3, yPos)
        yPos += nameLines.length * 4

        doc.setFont('helvetica', 'normal')
        doc.setTextColor(100, 100, 100)
        doc.text(`Host: ${vuln.host}:${vuln.port}`, margin + 3, yPos)
        yPos += 4

        if (vuln.description) {
          doc.setTextColor(70, 70, 70)
          const descLines = doc.splitTextToSize(vuln.description.substring(0, 200), contentWidth - 10)
          doc.text(descLines.slice(0, 2), margin + 3, yPos)
          yPos += Math.min(descLines.length, 2) * 4
        }
        yPos += 3
        doc.setTextColor(50, 50, 50)
      }
      if (vulns.length > 20) {
        doc.setTextColor(100, 100, 100)
        doc.text(`... and ${vulns.length - 20} more ${severity} vulnerabilities`, margin + 3, yPos)
        yPos += 6
      }
      yPos += 4
    }
  }

  // Footer
  const totalPages = doc.internal.pages.length - 1
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text(`Generated by Clarantis GRC Platform`, margin, pageHeight - 8)
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 8)
  }

  return doc.output('blob')
}

// Export Dropdown Component (#185)
function ExportDropdown({ resultId }: { resultId: string }) {
  const [isOpen, setIsOpen] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)
  const toast = useToast()

  const handleExport = async (format: 'json' | 'csv' | 'pdf') => {
    setExporting(format)
    try {
      if (format === 'json') {
        // For JSON, fetch and download as file
        const response = await api.exportScanResult(resultId, 'json')
        if (response.success && 'data' in response) {
          const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `scan-report-${resultId.slice(0, 8)}.json`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
          toast.success('JSON export downloaded')
        }
      } else if (format === 'csv') {
        // For CSV, use the download endpoint
        await api.downloadScanExport(resultId, 'csv')
        toast.success('CSV export downloaded')
      } else if (format === 'pdf') {
        // For PDF, generate using jsPDF
        const response = await api.exportScanResult(resultId, 'json')
        if (response.success && 'data' in response) {
          const exportData = response.data
          // Transform data to match generateScanReportPDF expectations
          const pdfData = {
            scan: {
              id: exportData.scan.id,
              config_name: exportData.scan.config_name,
              scan_type: exportData.scan.scan_type,
              status: exportData.scan.status,
              started_at: exportData.scan.started_at,
              completed_at: exportData.scan.completed_at,
              hosts_scanned: exportData.scan.hosts_scanned,
            },
            summary: exportData.summary,
            discovered_hosts: exportData.discovered_hosts.map(h => ({
              ip: h.ip,
              hostname: h.hostname || h.ip,
              state: h.state,
              ports: h.ports.map(p => ({
                port: p.port,
                protocol: p.protocol,
                state: 'open',
                service: p.service || undefined,
              })),
            })),
            vulnerabilities: exportData.vulnerabilities.map(v => ({
              host: v.host,
              port: v.port || 0,
              vulnerability_name: v.vulnerability_name,
              severity: v.severity,
              description: v.description || '',
              solution: v.solution || undefined,
            })),
          }

          const pdfBlob = await generateScanReportPDF(pdfData)
          const url = URL.createObjectURL(pdfBlob)
          const a = document.createElement('a')
          a.href = url
          a.download = `scan-report-${resultId.slice(0, 8)}.pdf`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
          toast.success('PDF report downloaded')
        } else {
          throw new Error('Failed to fetch scan data for PDF')
        }
      }
    } catch (error) {
      console.error('Export failed:', error)
      toast.error(`Failed to export ${format.toUpperCase()}`)
    } finally {
      setExporting(null)
      setIsOpen(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Download className="h-4 w-4" />
        Export
        <ChevronDown className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
            <button
              onClick={() => handleExport('json')}
              disabled={exporting !== null}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <FileJson className="h-4 w-4 text-blue-500" />
              {exporting === 'json' ? 'Exporting...' : 'Export JSON'}
            </button>
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting !== null}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <FileSpreadsheet className="h-4 w-4 text-green-500" />
              {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting !== null}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <FileText className="h-4 w-4 text-red-500" />
              {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// Scan Comparison Modal (#101)
function CompareModal({
  results,
  onClose,
}: {
  results: ScanResult[]
  onClose: () => void
}) {
  const { formatDate, formatDateTime } = useDatePreferences()
  const [scan1Id, setScan1Id] = useState<string>('')
  const [scan2Id, setScan2Id] = useState<string>('')
  const [comparison, setComparison] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  const completedResults = results.filter(r => r.status === 'completed')

  const handleCompare = async () => {
    if (!scan1Id || !scan2Id) {
      toast.warning('Please select two scans to compare')
      return
    }
    if (scan1Id === scan2Id) {
      toast.warning('Please select two different scans')
      return
    }

    setLoading(true)
    try {
      const response = await api.compareScanResults(scan1Id, scan2Id)
      if (response.success) {
        setComparison(response.comparison)
      } else {
        toast.error('Failed to compare scans')
      }
    } catch (error) {
      console.error('Comparison failed:', error)
      toast.error('Failed to compare scans')
    } finally {
      setLoading(false)
    }
  }

  const scan1 = completedResults.find(r => r.id === scan1Id)
  const scan2 = completedResults.find(r => r.id === scan2Id)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <GitCompare className="h-6 w-6 text-primary-600" />
            <h2 className="text-xl font-bold text-gray-900">Compare Scan Results</h2>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scan Selection */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Baseline Scan (Older)</label>
              <select
                value={scan1Id}
                onChange={(e) => {
                  setScan1Id(e.target.value)
                  setComparison(null)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a scan...</option>
                {completedResults.map(r => (
                  <option key={r.id} value={r.id} disabled={r.id === scan2Id}>
                    {r.config_name} - {formatDate(r.started_at)} ({r.vulnerabilities_found} vulns)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Scan (Newer)</label>
              <select
                value={scan2Id}
                onChange={(e) => {
                  setScan2Id(e.target.value)
                  setComparison(null)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a scan...</option>
                {completedResults.map(r => (
                  <option key={r.id} value={r.id} disabled={r.id === scan1Id}>
                    {r.config_name} - {formatDate(r.started_at)} ({r.vulnerabilities_found} vulns)
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleCompare}
              disabled={!scan1Id || !scan2Id || loading}
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Comparing...
                </>
              ) : (
                <>
                  <GitCompare className="h-4 w-4" />
                  Compare Scans
                </>
              )}
            </button>
          </div>
        </div>

        {/* Comparison Results */}
        <div className="flex-1 overflow-y-auto p-6">
          {!comparison ? (
            <div className="text-center py-12 text-gray-500">
              <GitCompare className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium">Select two scans to compare</p>
              <p className="text-sm mt-1">See what vulnerabilities are new, resolved, or unchanged</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 text-red-600 mb-2">
                    <TrendingUp className="h-5 w-5" />
                    <span className="text-2xl font-bold">{comparison.new_vulnerabilities?.length || 0}</span>
                  </div>
                  <p className="text-sm text-red-700 font-medium">New Vulnerabilities</p>
                  <p className="text-xs text-red-600 mt-1">Found in current scan only</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                    <TrendingDown className="h-5 w-5" />
                    <span className="text-2xl font-bold">{comparison.resolved_vulnerabilities?.length || 0}</span>
                  </div>
                  <p className="text-sm text-green-700 font-medium">Resolved</p>
                  <p className="text-xs text-green-600 mt-1">Fixed since baseline scan</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
                    <Minus className="h-5 w-5" />
                    <span className="text-2xl font-bold">{comparison.unchanged_vulnerabilities?.length || 0}</span>
                  </div>
                  <p className="text-sm text-gray-700 font-medium">Unchanged</p>
                  <p className="text-xs text-gray-600 mt-1">Still present in both scans</p>
                </div>
              </div>

              {/* Scan Comparison Details */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-2 divide-x divide-gray-200">
                  <div className="p-4 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Baseline Scan</h4>
                    <p className="text-sm text-gray-600">{scan1?.config_name}</p>
                    <p className="text-xs text-gray-500">{scan1 && formatDateTime(scan1.started_at)}</p>
                    <p className="text-xs text-gray-500 mt-1">{comparison.scan1?.vulnerabilities_found || 0} vulnerabilities</p>
                  </div>
                  <div className="p-4 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Scan</h4>
                    <p className="text-sm text-gray-600">{scan2?.config_name}</p>
                    <p className="text-xs text-gray-500">{scan2 && formatDateTime(scan2.started_at)}</p>
                    <p className="text-xs text-gray-500 mt-1">{comparison.scan2?.vulnerabilities_found || 0} vulnerabilities</p>
                  </div>
                </div>
              </div>

              {/* New Vulnerabilities */}
              {comparison.new_vulnerabilities?.length > 0 && (
                <div>
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-red-700 mb-3">
                    <TrendingUp className="h-4 w-4" />
                    New Vulnerabilities ({comparison.new_vulnerabilities.length})
                  </h4>
                  <div className="space-y-2">
                    {comparison.new_vulnerabilities.map((vuln: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          vuln.severity === 'critical' ? 'bg-red-100 text-red-700' :
                          vuln.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                          vuln.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {vuln.severity?.toUpperCase()}
                        </span>
                        <span className="flex-1 text-sm text-gray-900">{vuln.vulnerability_name}</span>
                        <span className="font-mono text-xs text-gray-500">{vuln.host}:{vuln.port}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resolved Vulnerabilities */}
              {comparison.resolved_vulnerabilities?.length > 0 && (
                <div>
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-green-700 mb-3">
                    <TrendingDown className="h-4 w-4" />
                    Resolved Vulnerabilities ({comparison.resolved_vulnerabilities.length})
                  </h4>
                  <div className="space-y-2">
                    {comparison.resolved_vulnerabilities.map((vuln: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          vuln.severity === 'critical' ? 'bg-red-100 text-red-700' :
                          vuln.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                          vuln.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {vuln.severity?.toUpperCase()}
                        </span>
                        <span className="flex-1 text-sm text-gray-900 line-through opacity-70">{vuln.vulnerability_name}</span>
                        <span className="font-mono text-xs text-gray-500">{vuln.host}:{vuln.port}</span>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unchanged (collapsible) */}
              {comparison.unchanged_vulnerabilities?.length > 0 && (
                <details className="group">
                  <summary className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer list-none">
                    <ChevronDown className="h-4 w-4 group-open:rotate-180 transition-transform" />
                    Unchanged Vulnerabilities ({comparison.unchanged_vulnerabilities.length})
                  </summary>
                  <div className="mt-3 space-y-2">
                    {comparison.unchanged_vulnerabilities.slice(0, 20).map((vuln: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          vuln.severity === 'critical' ? 'bg-red-100 text-red-700' :
                          vuln.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                          vuln.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {vuln.severity?.toUpperCase()}
                        </span>
                        <span className="flex-1 text-sm text-gray-700">{vuln.vulnerability_name}</span>
                        <span className="font-mono text-xs text-gray-500">{vuln.host}:{vuln.port}</span>
                      </div>
                    ))}
                    {comparison.unchanged_vulnerabilities.length > 20 && (
                      <p className="text-sm text-gray-500 text-center py-2">
                        ... and {comparison.unchanged_vulnerabilities.length - 20} more
                      </p>
                    )}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
